/**
 * Domain Card Configuration
 *
 * This file defines the behavior and UI patterns for all domain cards with
 * special mechanics. Cards not in this config use the default display-only pattern.
 *
 * Pattern Types:
 * - TokenSpend: Simple token-spend cards (Strategic Approach, Inspirational Words)
 * - SingleAction: One button with activation state (I See It Coming, Deft Maneuvers, Signature Move)
 * - DualButton: Two inline action buttons (Conjure Swarm, Towering Stalk, Mending Touch, etc.)
 * - SplitButton: Action + toggle in split layout (Gifted Tracker)
 */

// ============================================================================
// PATTERN DEFINITIONS
// ============================================================================

export const PATTERN_TYPES = {
  TOKEN_SPEND: 'TokenSpend',
  SINGLE_ACTION: 'SingleAction',
  DUAL_BUTTON: 'DualButton',
  SPLIT_BUTTON: 'SplitButton',
  TRIPLE_BUTTON: 'TripleButton',
  PROGRESSIVE_DIE: 'ProgressiveDie', // Wild Surge, Zone of Protection
  LAYER_DEFENSE: 'LayerDefense', // Confusing Aura
  ACCUMULATOR: 'Accumulator', // Sigil of Retribution
  COUNTDOWN: 'Countdown', // Mass Disguise
  SUMMONER: 'Summoner', // Forest Sprites
  GRIMOIRE: 'Grimoire', // Codex grimoires with multiple sub-spells
  ALLY_TARGET: 'AllyTarget', // I Am Your Shield, Lean On Me - party targeting
  ALLY_HEALING: 'AllyHealing', // Mending Touch - target ally + choose heal mode
  ALLY_EFFECT_CHOICE: 'AllyEffectChoice', // Inspirational Words - target ally + choose effect
  SPELLCAST_HEALING: 'SpellcastHealing', // Healing Hands - spellcast roll with healing on success/failure
  PARTY_BUFF: 'PartyBuff', // Battle Cry - broadcast effects to all party members
  CRIT_TRIGGER: 'CritTrigger', // Critical Inspiration, Rousing Strike - triggered on crit
  LIFE_WARD: 'LifeWard', // Life Ward - death move protection
  DOMAIN_TOUCHED: 'DomainTouched', // Domain-Touched cards - require 4+ cards from same domain
  TRANSFORMATION: 'Transformation', // Frenzy, Specter of the Dark, Force of Nature - state-changing transformations
  PERMANENT_CHOICE: 'PermanentChoice', // Vitality, Master of the Craft - one-time permanent bonuses, then vault
};

// ============================================================================
// DOMAIN CARD CONFIGURATIONS
// ============================================================================

export const DOMAIN_CARD_CONFIG = {
  // ---------------------------------------------------------------------------
  // TokenSpend Pattern Cards
  // ---------------------------------------------------------------------------

  'strategic-approach': {
    pattern: PATTERN_TYPES.TRIPLE_BUTTON,
    cardType: 'Ability',
    tokenId: 'strategy-tokens',
    getMaxTokens: (character) => character?.traits?.Knowledge || 1,
    actions: {
      advantage: {
        label: 'Advantage',
        icon: '⚔️',
        description: 'Toggle advantage on next attack (spend 1 token)',
        setsAdvantage: true, // Wire to DiceRoller's hasAdvantage
      },
      allyStress: {
        label: 'Ally -1S',
        icon: '💪',
        description: 'Ally within Melee range of target clears 1 Stress (spend 1 token)',
      },
      damageBonus: {
        label: '+1d8',
        icon: '🎲',
        description: 'Add 1d8 to your next damage roll (spend 1 token)',
        addsBonusDice: { count: 1, size: 8 }, // Wire to DiceRoller damage bonus
      },
    },
    description: 'On first attack after moving Close to adversary: spend token for advantage, ally stress clear, or +1d8 damage.',
    // Config-driven token definition (Phase 6B proof of concept)
    tokenConfig: {
      id: 'strategy-tokens',
      name: 'Strategy Tokens',
      getMax: (char) => Math.max(1, char?.traits?.Knowledge || 1),
      refreshOn: 'long-rest',
    },
  },

  'inspirational-words': {
    pattern: PATTERN_TYPES.ALLY_EFFECT_CHOICE,
    cardType: 'Support',
    tokenId: 'inspiration-tokens',
    getMaxTokens: (character) => character?.traits?.Presence || 2,
    effects: [
      {
        id: 'clearStress',
        type: 'clearStress',
        amount: 1,
        label: '-1 Stress',
        icon: '💙',
        variant: 'support',
        description: 'Ally clears 1 Stress',
        successMessage: 'Cleared 1 Stress!'
      },
      {
        id: 'clearHP',
        type: 'clearHP',
        amount: 1,
        label: '+1 HP',
        icon: '💚',
        variant: 'healing',
        description: 'Ally heals 1 HP',
        successMessage: 'Healed 1 HP!'
      },
      {
        id: 'grantHope',
        type: 'grantHope',
        amount: 1,
        label: '+1 Hope',
        icon: '✨',
        variant: 'hope',
        description: 'Ally gains 1 Hope',
        successMessage: 'Gained 1 Hope!'
      }
    ],
    description: 'Select ally, spend token: clear 1 Stress, heal 1 HP, or grant 1 Hope. Tokens refresh on long rest.',
    tokenConfig: {
      id: 'inspiration-tokens',
      name: 'Inspiration Tokens',
      getMax: (char) => Math.max(1, char?.traits?.Presence || 1),
      refreshOn: 'long-rest',
    },
  },

  // ---------------------------------------------------------------------------
  // SingleAction Pattern Cards
  // ---------------------------------------------------------------------------

  'i-see-it-coming': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    cost: { type: 'stress', amount: 1 },
    sessionStateKey: 'iSeeItComingBoost',
    activeDuration: 8000, // 8 seconds
    activeStateKey: 'bonus',
    validation: {
      preventIfActive: true,
    },
    // Handler rolls d4, stores boost in character.iSeeItComingBoost for vitals bar display
    handler: 'activateISeeItComing',
    clearHandler: 'clearISeeItComingBoost', // Called when effect expires
    buttonLabel: {
      default: '+d4 (1 Stress)',
      active: (value) => `Active: +${value}`,
      disabled: {
        noStress: 'No Stress',
        active: 'Active',
      },
    },
    description: 'When targeted by attack beyond Melee range, mark Stress to roll d4 and add to Evasion.',
    activeIndicator: {
      color: 'purple',
      message: (evasion, bonus) => `Evasion boosted: ${evasion} + ${bonus} = ${evasion + bonus} (fades after 8s)`,
    },
  },

  'deft-maneuvers': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Movement',
    cost: { type: 'stress', amount: 1 },
    tokenId: 'deft-maneuvers-uses',
    handler: 'activateDeftManeuvers',
    sessionStateKey: 'deftManeuversActive', // Wired to DiceRoller for +1 attack bonus
    validation: {
      checkToken: true,
    },
    buttonLabel: {
      default: 'Sprint (1S)',
      active: '+1 Attack Ready',
      disabled: {
        noStress: 'No Stress',
        noToken: 'Used',
      },
    },
    buttonIcon: '🏃',
    description: 'Once per rest: Sprint to Far (no Agility Roll). +1 attack if ending in Melee.',
    tokenConfig: {
      id: 'deft-maneuvers-uses',
      name: 'Deft Maneuvers',
      max: 1,
      refreshOn: 'rest',
      requiresActivation: true,
      cost: { type: 'stress', amount: 1 },
    },
  },

  'signature-move': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Attack',
    tokenId: 'signature-move-uses',
    handler: 'activateSignatureMove',
    sessionStateKey: 'signatureMoveActive', // Wired to DiceRoller for d20 Hope Die
    validation: {
      checkToken: true,
    },
    buttonLabel: {
      default: 'Use (d20 Hope)',
      active: 'd20 Hope Ready',
      disabled: {
        noToken: 'Used',
      },
    },
    buttonIcon: '⚔️',
    description: 'Once per rest: Roll d20 as Hope Die. On success, clear 1 Stress.',
    tokenConfig: {
      id: 'signature-move-uses',
      name: 'Signature Move',
      max: 1,
      refreshOn: 'rest',
      requiresActivation: true,
    },
  },

  // ---------------------------------------------------------------------------
  // SingleAction Pattern Cards - C2 Mark Stress (Issue #4)
  // ---------------------------------------------------------------------------

  // BLADE DOMAIN
  'reckless': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 2,
    cost: { type: 'stress', amount: 1 },
    sessionStateKey: 'recklessAdvantage',
    buttonLabel: {
      default: 'Reckless (1S)',
      active: 'Advantage Ready',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '⚔️',
    description: 'Mark Stress to gain advantage on an attack.',
  },

  'versatile-fighter': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 3,
    cost: { type: 'stress', amount: 1 },
    sessionStateKey: 'versatileFighterMaxDamage',
    passiveDescription: 'You can use a different character trait for an equipped weapon.',
    buttonLabel: {
      default: 'Max Damage Die (1S)',
      active: 'Max Damage Ready',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '🎲',
    description: 'Passive: Use any trait for weapon. Active: Mark Stress to use max result on one damage die.',
  },

  'onslaught': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 10,
    cost: { type: 'stress', amount: 1 },
    passiveDescription: 'Attacks always deal at least Major damage (min 2 HP marked).',
    buttonLabel: {
      default: 'Force Reaction (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '⚔️',
    description: 'Passive: Min Major damage. Reactive: When ally attacked, 1 Stress forces enemy Reaction (15) or mark HP.',
  },

  // BONE DOMAIN
  'boost': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 4,
    cost: { type: 'stress', amount: 1 },
    sessionStateKey: 'boostActive',
    buttonLabel: {
      default: 'Boost Attack (1S)',
      active: 'Aerial Attack Ready',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '🚀',
    description: 'Mark Stress: boost off ally, aerial attack vs Far target. Advantage + d10 damage.',
  },

  'brace': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 3,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: 'Brace (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '🛡️',
    description: 'When marking Armor Slot, mark Stress to mark an additional Armor Slot.',
  },

  // GRACE DOMAIN
  'astral-projection': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 8,
    cost: { type: 'stress', amount: 1 },
    tokenId: 'astral-projection-charge',
    refreshTrigger: 'long-rest',
    sessionStateKey: 'astralProjectionActive',
    validation: { checkToken: true },
    buttonLabel: {
      default: 'Project (1S)',
      active: 'Projection Active',
      disabled: {
        noStress: 'No Stress',
        noToken: 'Used This Long Rest',
      },
    },
    buttonIcon: '👻',
    description: 'Once/long rest, 1 Stress: Create projection anywhere you\'ve been. See/act through it until rest or damaged.',
  },

  // MIDNIGHT DOMAIN
  'stealth-expertise': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'midnight',
    level: 4,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: 'Convert Fear to Hope (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '🤫',
    description: 'When rolling Fear on stealth, mark Stress to roll with Hope instead (self or Close ally).',
  },

  // VALOR DOMAIN
  'full-surge': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'valor',
    level: 8,
    cost: { type: 'stress', amount: 3 },
    tokenId: 'full-surge-charge',
    refreshTrigger: 'long-rest',
    sessionStateKey: 'fullSurgeActive',
    validation: { checkToken: true },
    buttonLabel: {
      default: 'Full Surge (3S)',
      active: '+2 All Traits Active',
      disabled: {
        noStress: 'Need 3 Stress',
        noToken: 'Used This Long Rest',
      },
    },
    buttonIcon: '💪',
    description: 'Once/long rest, 3 Stress: +2 to ALL character traits until next rest.',
  },

  // ---------------------------------------------------------------------------
  // SingleAction Pattern Cards - C3 Once per Rest (Issue #4)
  // ---------------------------------------------------------------------------

  // ARCANA DOMAIN
  'premonition': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 5,
    tokenId: 'premonition-charge',
    refreshTrigger: 'long-rest',
    validation: { checkToken: true },
    buttonLabel: {
      default: 'Undo Consequences',
      disabled: { noToken: 'Used This Long Rest' },
    },
    buttonIcon: '🔮',
    description: 'Once/long rest: After GM conveys consequences, rescind move and retry.',
  },

  // BLADE DOMAIN
  'a-soldiers-bond': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 2,
    tokenId: 'soldiers-bond-charge',
    refreshTrigger: 'long-rest',
    validation: { checkToken: true },
    buttonLabel: {
      default: 'Compliment (+3 Hope Both)',
      disabled: { noToken: 'Used This Long Rest' },
    },
    buttonIcon: '🤝',
    description: 'Once/long rest: Compliment someone or ask about their skill, you both gain 3 Hope.',
  },

  'deadly-focus': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 4,
    tokenId: 'deadly-focus-charge',
    refreshTrigger: 'rest',
    sessionStateKey: 'deadlyFocusActive',
    validation: { checkToken: true },
    buttonLabel: {
      default: 'Focus Target (+1 Prof)',
      active: '+1 Proficiency Active',
      disabled: { noToken: 'Used This Rest' },
    },
    buttonIcon: '🎯',
    description: 'Once/rest: +1 Proficiency until you attack different target, defeat target, or battle ends.',
  },

  // BONE DOMAIN
  'splintering-strike': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 9,
    cost: { type: 'hope', amount: 1 },
    tokenId: 'splintering-strike-charge',
    refreshTrigger: 'long-rest',
    validation: { checkToken: true, requireHope: 1 },
    buttonLabel: {
      default: 'Splintering Strike (1H)',
      disabled: {
        noHope: 'Need 1 Hope',
        noToken: 'Used This Long Rest',
      },
    },
    buttonIcon: '💥',
    description: 'Once/long rest, 1 Hope: Attack all in weapon range. Distribute damage + bonus die per target.',
  },

  // GRACE DOMAIN
  'troublemaker': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'grace',
    level: 2,
    tokenId: 'troublemaker-charge',
    refreshTrigger: 'rest',
    validation: { checkToken: true },
    triggersPendingSpellAttack: {
      rollType: 'trait',
      trait: 'Presence',
      difficulty: null, // Contested roll vs target
      spellName: 'Troublemaker',
      successMessage: '😈 Taunt succeeded!',
      failureMessage: 'Taunt failed — target resisted!',
      followUpDice: {
        die: 'd4',
        usesProficiency: true, // SRD: "roll a number of d4s equal to your Proficiency"
        takeHighest: true, // SRD: "mark Stress equal to the highest result rolled"
        label: 'Troublemaker',
        resultLabel: 'Stress',
      },
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '😈 Taunt (Prof × d4 Stress)',
      disabled: { noToken: 'Used This Rest' },
    },
    buttonIcon: '😈',
    description: 'Once/rest: Presence Roll vs Far target. Success: roll Prof x d4, target marks highest as Stress.',
  },

  // MIDNIGHT DOMAIN
  'night-terror': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 9,
    tokenId: 'night-terror-charge',
    refreshTrigger: 'long-rest',
    validation: { checkToken: true },
    buttonLabel: {
      default: 'Night Terror',
      disabled: { noToken: 'Used This Long Rest' },
    },
    buttonIcon: '👹',
    description: 'Once/long rest: Very Close targets Reaction (16) or Horrified+Vulnerable. Steal Fear, deal d6 per Fear stolen.',
  },

  // ---------------------------------------------------------------------------
  // DualButton Pattern Cards
  // ---------------------------------------------------------------------------

  'conjure-swarm': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    primaryAction: {
      id: 'beetles',
      label: 'Beetles (1S)',
      icon: '🪲',
      cost: { type: 'stress', amount: 1 },
      sessionStateKey: 'beetlesActive',
      validation: { preventIfActive: true },
      customHandler: true, // Uses session-local state
      activeLabel: 'Beetles Active',
      description: 'Conjure armored beetles. Next damage reduces severity by 1 threshold.',
    },
    secondaryAction: {
      id: 'fireFlies',
      label: 'Fire Flies',
      icon: '🔥',
      variant: 'attack',
      triggersPendingSpellAttack: {
        difficulty: null, // Contested roll per SRD ("against nearby Adversaries")
        damage: '2d8+3',
        damageType: 'magic',
        hopeCost: 1,
      },
      switchToTab: 'combat',
      validation: { requireHope: 1 },
      description: 'Spellcast Roll against Close adversaries. Spend Hope for 2d8+3 magic damage.',
    },
    description: 'Beetles: Absorb damage (1S). Fire Flies: AoE magic attack (1H).',
  },

  'towering-stalk': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    primaryAction: {
      id: 'utility',
      label: 'Conjure',
      icon: '🌿',
      tokenId: 'stalk-utility-uses',
      handler: 'activateToweringStalkUtility',
      validation: { checkToken: true },
      disabledLabel: 'Used',
      description: 'Conjure climbable stalk up to Far range height (once per rest).',
    },
    secondaryAction: {
      id: 'attack',
      label: 'Attack (1S)',
      icon: '🌿',
      variant: 'attack',
      cost: { type: 'stress', amount: 1 },
      autoTriggerPrimaryFirst: true, // Auto-conjure stalk before attacking if available
      triggersPendingSpellAttack: {
        difficulty: 12,
        damage: 'd8',
        damageType: 'physical',
        hopeCost: 0,
        usesProficiency: true, // Roll proficiency number of d8s per SRD
      },
      switchToTab: 'combat',
      description: 'Mark 1 Stress for Spellcast Roll, Proficiency×d8 physical damage (unlimited).',
    },
    description: 'Utility: Climbable stalk (once/rest). Attack: Proficiency×d8 physical (1S, unlimited).',
    tokenConfig: {
      id: 'stalk-utility-uses',
      name: 'Towering Stalk (Utility)',
      max: 1,
      refreshOn: 'rest',
    },
  },

  'mending-touch': {
    pattern: PATTERN_TYPES.ALLY_HEALING,
    cardType: 'Spell',
    primaryAction: {
      id: 'normal',
      label: 'Heal 1 (2H)',
      cost: { type: 'hope', amount: 2 },
      description: 'Spend 2 Hope to heal 1 HP from ally.',
    },
    secondaryAction: {
      id: 'enhanced',
      label: 'Heal 2',
      icon: '⭐',
      cost: { type: 'hope', amount: 2 },
      tokenId: 'mending-touch-enhanced',
      description: 'Spend 2 Hope to heal 2 HP from ally (once per long rest).',
    },
    description: 'Select ally, then choose heal mode. Enhanced mode doubles healing.',
    tokenConfig: {
      id: 'mending-touch-enhanced',
      name: 'Mending Touch (Enhanced)',
      max: 1,
      refreshOn: 'long-rest',
      requiresActivation: true,
      cost: { type: 'hope', amount: 2 },
    },
  },

  /**
   * Healing Hands (SPLENDOR L2)
   * SRD: Make a Spellcast Roll (13) and target a creature other than yourself within Melee range.
   * On a success, mark a Stress to clear 2 Hit Points or 2 Stress on the target.
   * On a failure, mark a Stress to clear a Hit Point or a Stress on the target.
   * You can't heal the same target again until your next long rest.
   */
  'healing-hands': {
    pattern: PATTERN_TYPES.SPELLCAST_HEALING,
    cardType: 'Spell',
    domain: 'splendor',
    level: 2,
    spellcastDC: 13,
    healSelf: false, // SRD: "target a creature other than yourself"
    stressCost: 1, // Mark 1 Stress on cast (success or failure)
    trackTargetsPerRest: true, // Can't heal same target twice per long rest
    successHealing: {
      amount: 2,
      options: ['hp', 'stress'], // Can heal HP or Stress
      message: 'Healed 2 HP or cleared 2 Stress',
    },
    failureHealing: {
      amount: 1,
      options: ['hp', 'stress'], // Can heal HP or Stress
      message: 'Healed 1 HP or cleared 1 Stress',
    },
    description: 'Select ally, cast Spellcast (13). Success: heal 2. Failure: heal 1. Once per target per rest.',
  },

  'healing-field': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    primaryAction: {
      id: 'normal',
      label: 'Heal All 1',
      icon: '🌿',
      tokenId: 'healing-field-uses',
      handler: 'activateHealingField',
      handlerArgs: ['normal'],
      validation: { checkToken: true },
      disabledLabel: 'Used',
      description: 'Conjure healing field: You and Close allies clear 1 HP (once per long rest).',
      broadcastEffect: { type: 'healing', amount: 1 },
    },
    secondaryAction: {
      id: 'enhanced',
      label: 'Heal All 2 (2H)',
      enhancedLabel: true,
      icon: '⭐',
      cost: { type: 'hope', amount: 2 },
      tokenId: 'healing-field-uses',
      handler: 'activateHealingField',
      handlerArgs: ['enhanced'],
      validation: { checkToken: true, requireHope: 2 },
      disabledLabel: 'Used',
      description: 'Spend 2 Hope: You and Close allies clear 2 HP.',
      broadcastEffect: { type: 'healing', amount: 2 },
    },
    description: 'AoE healing for you and all Close range allies. Once per long rest.',
    tokenConfig: {
      id: 'healing-field-uses',
      name: 'Healing Field',
      max: 1,
      refreshOn: 'long-rest',
      requiresActivation: true,
    },
  },

  'second-wind': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Triggered',
    primaryAction: {
      id: 'stress',
      label: 'Clear 3S',
      icon: '💨',
      tokenId: 'second-wind-uses',
      sessionStateKey: 'secondWindPending',
      sessionStateValue: { type: 'stress' },
      validation: { checkToken: true, preventIfActive: true },
      activeLabel: 'Armed (3S)',
      disabledLabel: 'Used',
      description: 'Arm to clear 3 Stress on next successful attack.',
    },
    secondaryAction: {
      id: 'hp',
      label: 'Heal 1HP',
      icon: '❤️',
      tokenId: 'second-wind-uses',
      sessionStateKey: 'secondWindPending',
      sessionStateValue: { type: 'hp' },
      validation: { checkToken: true, preventIfActive: true },
      activeLabel: 'Armed (1HP)',
      disabledLabel: 'Used',
      description: 'Arm to heal 1 HP on next successful attack.',
    },
    description: 'Once per rest: On successful attack, clear 3 Stress OR heal 1 HP. With Hope: Ally also heals.',
    tokenConfig: {
      id: 'second-wind-uses',
      name: 'Second Wind',
      max: 1,
      refreshOn: 'rest',
      requiresActivation: true,
    },
  },

  // ---------------------------------------------------------------------------
  // SplitButton Pattern Cards
  // ---------------------------------------------------------------------------

  'gifted-tracker': {
    pattern: PATTERN_TYPES.SPLIT_BUTTON,
    cardType: 'Tracking',
    mainAction: {
      label: 'Track (1H)',
      cost: { type: 'hope', amount: 1 },
      description: 'Spend 1 Hope to track a creature and activate +1 Evasion',
    },
    toggleAction: {
      stateKey: 'giftedTrackerActive',
      activeIcon: '❌',
      inactiveIcon: '🎯',
      description: 'Toggle +1 Evasion bonus vs tracked creatures',
    },
    description: 'Track creature to ask GM questions. Toggle: +1 Evasion vs tracked.',
  },

  // ---------------------------------------------------------------------------
  // ProgressiveDie Pattern Cards
  // ---------------------------------------------------------------------------

  'wild-surge': {
    pattern: PATTERN_TYPES.PROGRESSIVE_DIE,
    cardType: 'Spell',
    domain: 'sage',
    level: 7,
    cost: { type: 'stress', amount: 1 },
    handler: 'activateWildSurge',
    incrementHandler: 'incrementWildSurgeDie',
    endHandler: 'endWildSurge',
    valueGetter: 'getWildSurgeDieValue',
    activeChecker: 'isWildSurgeActive',
    sessionStateKey: 'wildSurgeDie',
    resourceKey: 'wild-surge-die', // Key in cardResources to check usedThisSession
    dieConfig: {
      startValue: 1,
      maxValue: 6,
      bonusType: 'action-roll', // Adds to all action rolls
    },
    endCost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: 'Channel Nature (1S)',
      active: (value) => `Wild Surge +${value}`,
      disabled: {
        noStress: 'No Stress',
        active: 'Active',
      },
    },
    activeIndicator: {
      color: 'green',
      message: (_, value) => `+${value} to all action rolls. Die increases after each roll.`,
    },
    description: 'Mark Stress to enhance yourself. +die value to action rolls, increases each roll.',
    flavorText: 'Describe how your appearance changes as you channel the natural world.',
    successIcon: '🌿',
    tokenConfig: {
      id: 'wild-surge-die',
      name: 'Wild Surge Die',
      type: 'progressive-die',
      max: 6,
      refreshOn: 'long-rest',
      requiresActivation: true,
      cost: { type: 'stress', amount: 1 },
      die: 'd6',
    },
  },

  'zone-of-protection': {
    pattern: PATTERN_TYPES.PROGRESSIVE_DIE,
    cardType: 'Spell',
    domain: 'splendor',
    level: 6,
    cost: { type: 'spellcast', dc: 16 }, // Requires Spellcast (16)
    handler: 'activateZoneOfProtection',
    useHandler: 'useZoneOfProtection',
    valueGetter: 'getZoneProtectionDieValue',
    activeChecker: 'isZoneOfProtectionActive',
    sessionStateKey: 'zoneProtectionDie',
    resourceKey: 'protection-die', // Key in cardResources to check usedThisSession
    dieConfig: {
      startValue: 1,
      maxValue: 6,
      bonusType: 'damage-reduction', // Reduces ally damage
    },
    buttonLabel: {
      default: 'Create Zone (16)',
      active: (value) => `Zone -${value} dmg`,
      disabled: {
        used: 'Used this rest',
        active: 'Active',
      },
    },
    activeIndicator: {
      color: 'yellow',
      message: (_, value) => `Allies in zone reduce damage by ${value}. Die increases after use.`,
    },
    description: 'Spellcast (16) to create protective zone. Allies reduce damage by die value.',
    range: { create: 'Far', effect: 'Very Close' },
    successIcon: '✨',
    tokenConfig: {
      id: 'protection-die',
      name: 'Zone Protection Die',
      type: 'progressive-die',
      max: 6,
      refreshOn: 'long-rest',
      requiresActivation: true,
      die: 'd6',
    },
  },

  // ---------------------------------------------------------------------------
  // LayerDefense Pattern Cards
  // ---------------------------------------------------------------------------

  'confusing-aura': {
    pattern: PATTERN_TYPES.LAYER_DEFENSE,
    cardType: 'Spell',
    domain: 'arcana',
    level: 8,
    cost: { type: 'spellcast', dc: 14 },
    handler: 'activateConfusingAura',
    layerGetter: 'getConfusingAuraLayers',
    activeChecker: 'isConfusingAuraActive',
    usedChecker: 'isConfusingAuraUsedThisRest',
    endHandler: 'endConfusingAura',
    refreshTrigger: 'long-rest',
    description: 'Spellcast (14) to create illusion layers. Mark Stress for extra layers. When attacked, roll d6 per layer - 5+ blocks attack.',
    successIcon: '✨',
    tokenConfig: {
      id: 'aura-layers',
      name: 'Aura Layers',
      max: 10,
      refreshOn: 'long-rest',
      requiresActivation: true,
    },
  },

  // ---------------------------------------------------------------------------
  // Accumulator Pattern Cards
  // ---------------------------------------------------------------------------

  'sigil-of-retribution': {
    pattern: PATTERN_TYPES.ACCUMULATOR,
    cardType: 'Spell',
    domain: 'codex',
    level: 6,
    description: 'Mark adversary (GM gains Fear). Gain d8 when target damages allies. Roll all d8s on attack for bonus damage.',
    successIcon: '🎯',
    tokenConfig: {
      id: 'retribution-dice',
      name: 'Retribution Dice',
      getMax: (char) => char?.level || 5,
      refreshOn: 'manual',
      requiresActivation: true,
      die: 'd8',
    },
  },

  // ---------------------------------------------------------------------------
  // Countdown Pattern Cards
  // ---------------------------------------------------------------------------

  'mass-disguise': {
    pattern: PATTERN_TYPES.COUNTDOWN,
    cardType: 'Spell',
    domain: 'midnight',
    level: 6,
    countdownMax: 8,
    cost: { type: 'stress', amount: 1 },
    description: 'Mark Stress to disguise all allies in Close range. Countdown ticks on GM consequences.',
    successIcon: '🎭',
    tokenConfig: {
      id: 'disguise-countdown',
      name: 'Disguise Countdown',
      max: 8,
      refreshOn: 'manual',
      requiresActivation: true,
      cost: { type: 'stress', amount: 1 },
    },
  },

  // ---------------------------------------------------------------------------
  // Summoner Pattern Cards
  // ---------------------------------------------------------------------------

  'forest-sprites': {
    pattern: PATTERN_TYPES.SUMMONER,
    cardType: 'Spell',
    domain: 'sage',
    level: 8,
    activationDC: 13, // Spellcast (13)
    description: 'Spellcast (13), spend Hope to summon sprites. Allies near sprites: +3 attack OR +1 Armor (sprite vanishes).',
    successIcon: '🧚',
    tokenConfig: {
      id: 'active-sprites',
      name: 'Forest Sprites',
      max: 99,
      refreshOn: 'manual',
      requiresActivation: true,
    },
  },

  // ---------------------------------------------------------------------------
  // GAME NIGHT SPRINT - Party Equipped Cards (Dec 31, 2025)
  // ---------------------------------------------------------------------------

  // TIER 1: QUICK WINS

  'wall-walk': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 1,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'wallWalkActive',
    buttonLabel: {
      default: '🕷️ Wall Walk (1H)',
      active: '✓ Wall Walk Active',
    },
    description: 'Spend Hope to let creature walk on walls/ceilings until end of scene or recast.',
  },

  'chokehold': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'midnight',
    level: 3,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '🤜 Chokehold (1S)',
    },
    description: 'Mark Stress to put target in chokehold. They become Vulnerable (+2d6 damage from attacks).',
  },

  'shadowbind': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 2,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested roll
      damage: null,
      damageType: null,
      isAoE: true,
      spellName: 'Shadowbind',
      successMessage: '👥 Shadowbind! Targets that fail are Restrained by their shadows.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '👥 Shadowbind',
    },
    description: 'Spellcast Roll against all Very Close adversaries. Successes are Restrained.',
  },

  'veil-of-night': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 3,
    sessionStateKey: 'veilOfNightActive', // For checking if active
    triggersPendingSpellAttack: {
      difficulty: 13,
      damage: null,
      damageType: null,
      spellName: 'Veil of Night',
      sessionStateOnSuccess: 'veilOfNightActive', // Set session state on success + Hidden condition
      successMessage: '🌑 Veil of Night! Hidden toggled ON. Advantage on attacks. Ends when you cast another spell.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌑 Veil of Night (DC 13)',
    },
    description: 'Spellcast (13): Create darkness curtain. You are Hidden and have advantage on attacks through it. Ends when you cast another spell.',
  },

  // TIER 2: MEDIUM COMPLEXITY

  'rain-of-blades': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 1,
    cost: { type: 'hope', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: 'd8+2',
      damageType: 'magic',
      isAoE: true,
      usesProficiency: true, // Roll Proficiency d8s, then +2
      spellName: 'Rain of Blades',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '⚔️ Rain of Blades (1H)',
    },
    description: 'Spend Hope, Spellcast Roll - Proficiency×d8+2 magic damage to Very Close targets. +1d8 if Vulnerable.',
  },

  'blink-out': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 4,
    cost: { type: 'hope', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: 12,
      damage: null,
      damageType: null,
      spellName: 'Blink Out',
      successMessage: '✨ Blink Out succeeded! Teleport Far. Spend +1 Hope per creature brought.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '✨ Blink Out (1H)',
    },
    description: 'Spellcast (12): Teleport Far. Spend +1 Hope per additional creature you bring along.',
  },

  // TIER 3: REUSE EXISTING PATTERNS

  'cinder-grasp': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'arcana',
    level: 2,
    // Primary: Main attack
    primaryAction: {
      id: 'attack',
      label: '🔥 Cinder Grasp',
      variant: 'attack',
      triggersPendingSpellAttack: {
        difficulty: null, // Contested melee spellcast
        damage: '1d20+3',
        damageType: 'magic',
        spellName: 'Cinder Grasp',
        successMessage: '🔥 Target is On Fire! (2d6 at end of their turn)',
      },
      switchToTab: 'combat',
      description: 'Spellcast Roll vs Melee target - 1d20+3 magic damage. Target is On Fire!',
    },
    // Secondary: Roll residual fire damage (GM tracks who is On Fire)
    secondaryAction: {
      id: 'fireDamage',
      label: '🔥 Fire (2d6)',
      variant: 'warning',
      rollDice: { count: 2, sides: 6, type: 'fire' },
      description: 'Roll 2d6 fire damage for a target that is On Fire.',
    },
    description: 'Attack: 1d20+3 magic, target On Fire. Use 2nd button for On Fire damage (2d6 at end of their turn).',
  },

  'counterspell': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 3,
    triggersPendingSpellAttack: {
      difficulty: null, // Reaction - contested against incoming spell
      damage: null,
      damageType: null,
      spellName: 'Counterspell',
      moveToVaultOnSuccess: true, // Card goes to vault after successful counter
      successMessage: '🛡️ Counterspell succeeded! Card moved to vault.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🛡️ Counterspell',
    },
    description: 'Reaction: Spellcast Roll vs spell. On success: spell fails, card moves to vault.',
  },

  // ---------------------------------------------------------------------------
  // AllyTarget Pattern Cards (Party Mechanics)
  // ---------------------------------------------------------------------------

  /**
   * I Am Your Shield (VALOR L1)
   * SRD: When an ally within Very Close range would take damage, you can mark
   * a Stress to stand in the way and make yourself the target of the attack instead.
   * When you take damage from this attack, you can mark any number of Armor Slots.
   */
  'i-am-your-shield': {
    pattern: PATTERN_TYPES.ALLY_TARGET,
    cardType: 'Ability',
    domain: 'valor',
    level: 1,
    action: {
      label: '🛡️ Protect Ally',
      variant: 'support',
      cost: { type: 'stress', amount: 1 },
      actionType: 'offerShield',
      showVitals: true,
      description: 'Stand in the way of damage meant for your ally.',
    },
    description: 'Mark Stress to intercept damage for a Very Close ally. Can use Armor Slots when hit.',
  },

  /**
   * Lean On Me (VALOR L3)
   * SRD: Once per long rest, when you console or inspire an ally who failed
   * an action roll, you can both clear 2 Stress.
   */
  'lean-on-me': {
    pattern: PATTERN_TYPES.ALLY_TARGET,
    cardType: 'Ability',
    domain: 'valor',
    level: 3,
    action: {
      label: '💙 Console Ally',
      variant: 'support',
      actionType: 'offerStressShare',
      maxAmount: 2,
      showVitals: true,
      description: 'Console an ally who failed - you both clear 2 Stress.',
    },
    description: 'Once per long rest: console a failing ally, both clear 2 Stress.',
  },

  // ---------------------------------------------------------------------------
  // REFRESHED IMPLEMENTATIONS (Dec 31, 2025)
  // ---------------------------------------------------------------------------

  /**
   * Unleash Chaos (ARCANA L1)
   * SRD: At session start, place tokens = Spellcast trait.
   * Make Spellcast Roll vs Far target, spend any number of tokens → roll that many d10s for magic damage.
   * Mark Stress to replenish tokens (up to Spellcast trait).
   * End of session: clear unspent tokens.
   */
  'unleash-chaos': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'arcana',
    level: 1,
    // Primary: Attack - spends tokens for damage
    primaryAction: {
      id: 'attack',
      label: '⚡ Unleash',
      variant: 'attack',
      tokenId: 'chaos-tokens',
      triggersPendingSpellAttack: {
        difficulty: null, // Contested
        damage: 'd10', // Variable - tokens determine dice count
        damageType: 'magic',
        spellName: 'Unleash Chaos',
        usesTokensAsDice: true, // Special: dice count = tokens spent
        tokenPrompt: true, // Prompt user for how many tokens to spend
      },
      switchToTab: 'combat',
      description: 'Spellcast Roll vs Far target. Spend tokens → roll that many d10s for magic damage.',
    },
    // Secondary: Replenish - marks Stress to refill tokens
    secondaryAction: {
      id: 'replenish',
      label: '😰 Replenish',
      variant: 'secondary',
      cost: { type: 'stress', amount: 1 },
      handler: 'replenishUnleashChaos',
      description: 'Mark 1 Stress to replenish tokens (up to Spellcast trait).',
    },
    description: 'Session tokens = Spellcast. Spend for d10s damage. Mark Stress to replenish.',
    tokenConfig: {
      id: 'chaos-tokens',
      name: 'Chaos Tokens',
      getMax: (char) => {
        // Use spellcast trait (varies by subclass)
        const traits = char?.traits || {};
        return Math.max(1, traits.Presence || traits.Instinct || traits.Knowledge || 2);
      },
      refreshOn: 'session',
    },
  },

  /**
   * Rune Ward (ARCANA L1)
   * SRD: Personal trinket held as ward. Holder spends 1 Hope to reduce incoming damage by 1d8.
   * If d8 result is 8, ward power ends after reducing damage this turn. Recharges on rest.
   */
  'rune-ward': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 1,
    cost: { type: 'hope', amount: 1 },
    tokenId: 'ward-charge',
    handler: 'activateRuneWard',
    validation: {
      checkToken: true,
    },
    buttonLabel: {
      default: '🛡️ Ward (-1d8 dmg)',
      disabled: {
        noToken: 'Ward Depleted',
        noHope: 'Need 1 Hope',
      },
    },
    description: 'Spend 1 Hope → roll 1d8 damage reduction. If you roll 8, ward depletes until rest.',
    successIcon: '🛡️',
    tokenConfig: {
      id: 'ward-charge',
      name: 'Rune Ward',
      max: 1,
      refreshOn: 'rest',
      requiresActivation: true,
      cost: { type: 'hope', amount: 1 },
    },
  },

  /**
   * Midnight Spirit (MIDNIGHT L2) - REFRESHED
   * SRD: Spend 1 Hope to summon humanoid spirit (utility - move/carry things until next rest).
   * Can send to attack: Spellcast Roll vs Very Far target. On success, roll Spellcast×d6 magic damage.
   * Spirit dissipates after attack. Only one spirit at a time.
   */
  'midnight-spirit': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'midnight',
    level: 2,
    // Primary: Summon the spirit (1 Hope, toggle)
    primaryAction: {
      id: 'summon',
      label: '👻 Summon (1H)',
      activeLabel: '👻 Spirit Active',
      cost: { type: 'hope', amount: 1 },
      customHandler: true, // Use built-in session state toggle
      sessionStateKey: 'midnightSpiritActive',
      description: 'Spend Hope to summon a floating spirit. It can move, carry things, or attack.',
    },
    // Secondary: Spirit attacks (consumes the spirit ON SUCCESS)
    secondaryAction: {
      id: 'attack',
      label: '⚔️ Spirit Attack',
      variant: 'attack',
      requiresPrimary: true, // Must have spirit summoned
      triggersPendingSpellAttack: {
        difficulty: null, // Contested roll
        damage: 'd6', // Base die, multiplied by Spellcast trait
        damageType: 'magic',
        spellName: 'Midnight Spirit',
        usesSpellcast: true, // Special: dice count = Spellcast trait value
        clearSessionStateOnSuccess: 'midnightSpiritActive', // Clear spirit only on SUCCESS
      },
      switchToTab: 'combat',
      // Note: Spirit dissipates AFTER successful attack, handled via clearSessionStateOnSuccess
      description: 'Spellcast Roll vs Very Far target → Spellcast×d6 magic damage. Spirit dissipates on success.',
    },
    description: 'Summon spirit (1H) for utility. Attack: Spellcast×d6 damage, spirit fades.',
  },

  /**
   * Corrosive Projectile (SAGE L3)
   * SRD: Spellcast Roll vs Far target → d6+4 magic damage.
   * Optional: Mark 2+ Stress to make target permanently Corroded (-1 Difficulty per 2 Stress).
   */
  'corrosive-projectile': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'sage',
    level: 3,
    // Primary: Basic corrosive attack (no cost)
    primaryAction: {
      id: 'attack',
      label: '🧪 Attack',
      variant: 'attack',
      triggersPendingSpellAttack: {
        difficulty: null, // vs target
        damage: 'd6+4',
        damageType: 'magic',
        spellName: 'Corrosive Projectile',
        usesProficiency: true, // SRD: "d6+4 magic damage using your Proficiency"
      },
      switchToTab: 'combat',
      description: 'Spellcast Roll vs Far target → d6+4 magic damage.',
    },
    // Secondary: Corrode attack (costs 2 Stress)
    secondaryAction: {
      id: 'corrode',
      label: '☠️ Corrode (2S)',
      variant: 'attack',
      cost: { type: 'stress', amount: 2 },
      triggersPendingSpellAttack: {
        difficulty: null, // vs target
        damage: 'd6+4',
        damageType: 'magic',
        spellName: 'Corrosive Projectile (Corrode)',
        usesProficiency: true, // SRD: "d6+4 magic damage using your Proficiency"
      },
      switchToTab: 'combat',
      successMessage: 'Target Corroded! -1 Difficulty permanently.',
      description: 'Mark 2 Stress → d6+4 damage + target gets -1 Difficulty (stacks).',
    },
    description: 'Spellcast vs Far → d6+4 magic. Mark 2S for permanent Corroded (-1 Difficulty).',
  },

  // ---------------------------------------------------------------------------
  // Grimoire Pattern Cards (Codex domain)
  // ---------------------------------------------------------------------------

  /**
   * Book of Ava (CODEX L1)
   * Three spells: Power Push, Tava's Armor, Ice Spike
   */
  'book-of-ava': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 1,
    spells: [
      {
        id: 'power-push',
        name: 'Power Push',
        icon: '💨',
        shortDescription: 'Spellcast vs Melee target → d10+2 magic, knockback to Far',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: '1d10+2',
          damageType: 'magic',
          usesProficiency: true, // SRD: "d10+2 magic damage using your Proficiency"
        },
        successMessage: 'Power Push! Target knocked to Far range.',
      },
      {
        id: 'tavas-armor',
        name: "Tava's Armor",
        icon: '🛡️',
        shortDescription: '1/session: +1 Armor until rest (1H)',
        cost: { type: 'hope', amount: 1 },
        oncePerRest: true,
        refreshOn: 'session',
        isUtility: true,
        requiresAllyTarget: true,
        selfTargetAllowed: true,
        armorBuff: { amount: 1, expiresOn: 'rest' },
      },
      {
        id: 'ice-spike',
        name: 'Ice Spike',
        icon: '🧊',
        shortDescription: 'Spellcast (DC 12) or vs target → d6 physical damage',
        triggersPendingSpellAttack: {
          difficulty: 12, // DC 12 for summoning, or contested
          damage: '1d6',
          damageType: 'physical',
          usesProficiency: true, // SRD: "d6 physical damage using your Proficiency"
        },
        successMessage: 'Ice Spike summoned!',
      },
    ],
    description: '**Power Push**: d10+2 knockback | **Tava\'s Armor**: +1 Armor (1H, 1/session) | **Ice Spike**: d6 physical',
    fullDescription: '**Power Push**: Make a Spellcast Roll against a target within Melee range. On a success, they\'re knocked back to Far range and take d10+2 magic damage using your Proficiency.\n\n**Tava\'s Armor**: Spend a Hope to give a target you can touch a +1 bonus to their Armor Score until their next rest or you cast Tava\'s Armor again.\n\n**Ice Spike**: Make a Spellcast Roll (12) to summon a large ice spike within Far range. If you use it as a weapon, make the Spellcast Roll against the target\'s Difficulty instead. On a success, deal d6 physical damage using your Proficiency.',
  },

  /**
   * Book of Illiat (CODEX L1)
   * Three spells: Slumber, Arcane Barrage, Telepathy
   */
  'book-of-illiat': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 1,
    spells: [
      {
        id: 'slumber',
        name: 'Slumber',
        icon: '😴',
        shortDescription: 'Spellcast vs Very Close target → Asleep until damaged',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: null, // no damage, just condition
        },
        successMessage: 'Target falls Asleep! (Until damaged or GM spends Fear)',
      },
      {
        id: 'arcane-barrage',
        name: 'Arcane Barrage',
        icon: '✨',
        shortDescription: '1/session: Spend Hope → d6 per Hope magic damage',
        variableCost: {
          type: 'hope',
          min: 1,
          max: null, // No max - spend as many as available
          label: 'Hope to spend',
          damagePerUnit: '1d6', // Roll this many d6
        },
        oncePerRest: true,
        refreshOn: 'session',
        triggersPendingSpellAttack: {
          difficulty: null, // auto-hits
          damageType: 'magic',
          // damage is calculated based on variableCost amount
        },
        successMessage: 'Arcane Barrage!',
      },
      {
        id: 'telepathy',
        name: 'Telepathy',
        icon: '🧠',
        shortDescription: 'Spend Hope → mental link with target until rest',
        cost: { type: 'hope', amount: 1 },
        isUtility: true,
        successMessage: 'Telepathic link established until rest!',
      },
    ],
    description: '**Slumber**: Sleep target | **Arcane Barrage**: d6/Hope (1/rest) | **Telepathy**: Mental link (1H)',
    fullDescription: '**Slumber**: Make a Spellcast Roll against a target within Very Close range. On a success, they\'re Asleep until they take damage or the GM spends a Fear on their turn to clear this condition.\n\n**Arcane Barrage**: Once per rest, spend any number of Hope and shoot magical projectiles that strike a target of your choice within Close range. Roll a number of d6s equal to the Hope spent and deal that much magic damage to the target.\n\n**Telepathy**: Spend a Hope to open a line of mental communication with one target you can see. This connection lasts until your next rest or you cast Telepathy again.',
  },

  /**
   * Book of Tyfar (CODEX L1)
   * Three spells: Wild Flame, Magic Hand, Mysterious Mist
   */
  'book-of-tyfar': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 1,
    spells: [
      {
        id: 'wild-flame',
        name: 'Wild Flame',
        icon: '🔥',
        shortDescription: 'Spellcast vs up to 3 Melee targets → 2d6 magic + they mark Stress',
        triggersPendingSpellAttack: {
          difficulty: null, // vs targets
          damage: '2d6',
          damageType: 'magic',
        },
        successMessage: 'Wild Flame! Targets take 2d6 magic and mark Stress.',
      },
      {
        id: 'magic-hand',
        name: 'Magic Hand',
        icon: '✋',
        shortDescription: 'Conjure a magical hand within Far range',
        isUtility: true,
        successMessage: 'Magic Hand conjured at Far range!',
      },
      {
        id: 'mysterious-mist',
        name: 'Mysterious Mist',
        icon: '🌫️',
        shortDescription: 'Spellcast DC 13 → create obscuring fog in Very Close area',
        triggersPendingSpellAttack: {
          difficulty: 13,
          damage: null, // no damage
        },
        successMessage: 'Mysterious Mist blankets the area!',
      },
    ],
    description: '**Wild Flame**: 2d6 magic + Stress | **Magic Hand**: Utility | **Mysterious Mist**: DC 13 fog',
    fullDescription: '**Wild Flame**: Make a Spellcast Roll against up to three adversaries within Melee range. Targets you succeed against take 2d6 magic damage and must mark a Stress as flames erupt from your hand.\n\n**Magic Hand**: You conjure a magical hand with the same size and strength as your own within Far range.\n\n**Mysterious Mist**: Make a Spellcast Roll (13) to cast a temporary thick fog that gathers in a stationary area within Very Close range. The fog heavily obscures this area and everything in it.',
  },

  /**
   * Book of Vagras (CODEX L2)
   * Three spells: Runic Lock, Arcane Door, Reveal
   */
  'book-of-vagras': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 2,
    spells: [
      {
        id: 'runic-lock',
        name: 'Runic Lock',
        icon: '🔒',
        shortDescription: 'Once/rest: Spellcast DC 15 → magically lock an object',
        triggersPendingSpellAttack: {
          difficulty: 15,
          damage: null,
        },
        oncePerRest: true,
        refreshOn: 'long-rest',
        successMessage: 'Runic Lock placed! Only chosen creatures can open.',
      },
      {
        id: 'arcane-door',
        name: 'Arcane Door',
        icon: '🚪',
        shortDescription: 'Spellcast DC 13 + 1 Hope → portal to Far range',
        cost: { type: 'hope', amount: 1 },
        triggersPendingSpellAttack: {
          difficulty: 13,
          damage: null,
        },
        successMessage: 'Arcane Door opens! One creature may pass through.',
      },
      {
        id: 'reveal',
        name: 'Reveal',
        icon: '👁️',
        shortDescription: 'Spellcast Roll → reveal magically hidden things in Close',
        triggersPendingSpellAttack: {
          difficulty: null, // vs hidden magic's DC
          damage: null,
        },
        successMessage: 'Reveal! Hidden magical things are exposed.',
      },
    ],
    description: '**Runic Lock**: DC 15 lock (1/rest) | **Arcane Door**: DC 13 portal (1H) | **Reveal**: Expose hidden',
    fullDescription: '**Runic Lock**: Make a Spellcast Roll (15) on an object you\'re touching that can close (such as a lock, chest, or box). Once per rest on a success, you can lock the object so it can only be opened by creatures of your choice. Someone with access to magic and an hour of time to study the spell can break it.\n\n**Arcane Door**: When you have no adversaries within Melee range, make a Spellcast Roll (13). On a success, spend a Hope to create a portal from where you are to a point within Far range you can see. It closes once a creature has passed through it.\n\n**Reveal**: Make a Spellcast Roll. If there is anything magically hidden within Close range the roll would succeed against, it is revealed.',
  },

  /**
   * Book of Norai (CODEX L3)
   * Two spells: Mystic Tether, Fireball
   */
  'book-of-norai': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 3,
    spells: [
      {
        id: 'mystic-tether',
        name: 'Mystic Tether',
        icon: '⛓️',
        shortDescription: 'Spellcast vs Far target → Restrained + mark Stress',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: null,
        },
        successMessage: 'Mystic Tether! Target is Restrained and marks Stress.',
      },
      {
        id: 'fireball',
        name: 'Fireball',
        icon: '☄️',
        shortDescription: 'Spellcast vs Very Far → d20+5 magic (AoE, half on save)',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: 'd20+5',
          damageType: 'magic',
          usesProficiency: true, // SRD: "d20+5 magic damage using your Proficiency"
          isAoE: true,
        },
        successMessage: 'Fireball! Targets in area make Reaction (13) or take full damage.',
      },
    ],
    description: '**Mystic Tether**: Restrain target | **Fireball**: d20+5 AoE magic',
    fullDescription: '**Mystic Tether**: Make a Spellcast Roll against a target within Far range. On a success, they\'re temporarily Restrained and must mark a Stress. If you target a flying creature, this spell grounds and temporarily Restrains them.\n\n**Fireball**: Make a Spellcast Roll against a target within Very Far range. On a success, hurl a sphere of fire toward them that explodes on impact. The target and all creatures within Very Close range of them must make a Reaction Roll (13). Targets who fail take d20+5 magic damage using your Proficiency. Targets who succeed take half damage.',
  },

  /**
   * Book of Grynn (CODEX L4)
   * Three spells: Arcane Deflection, Time Lock, Wall of Flame
   */
  'book-of-grynn': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 4,
    spells: [
      {
        id: 'arcane-deflection',
        name: 'Arcane Deflection',
        icon: '🛡️',
        shortDescription: 'Once/long rest + 1 Hope → negate attack damage',
        cost: { type: 'hope', amount: 1 },
        oncePerRest: true,
        refreshOn: 'long-rest',
        isUtility: true,
        successMessage: 'Arcane Deflection! Attack damage negated.',
      },
      {
        id: 'time-lock',
        name: 'Time Lock',
        icon: '⏱️',
        shortDescription: 'Freeze object in time/space until rest',
        isUtility: true,
        successMessage: 'Time Lock! Object frozen in place until rest.',
      },
      {
        id: 'wall-of-flame',
        name: 'Wall of Flame',
        icon: '🔥',
        shortDescription: 'Spellcast DC 15 → fire wall, 4d10+3 magic to passers',
        triggersPendingSpellAttack: {
          difficulty: 15,
          damage: '4d10+3',
          damageType: 'magic',
        },
        successMessage: 'Wall of Flame created! 4d10+3 magic to anything passing through.',
      },
    ],
    description: '**Arcane Deflection**: Negate damage (1H, 1/rest) | **Time Lock**: Freeze object | **Wall of Flame**: DC 15, 4d10+3',
    fullDescription: '**Arcane Deflection**: Once per long rest, spend a Hope to negate the damage of an attack targeting you or an ally within Very Close range.\n\n**Time Lock**: Target an object within Far range. That object stops in time and space exactly where it is until your next rest. If a creature tries to move it, make a Spellcast Roll against them to maintain this spell.\n\n**Wall of Flame**: Make a Spellcast Roll (15). On a success, create a temporary wall of magical flame between two points within Far range. All creatures in its path must choose a side to be on, and anything that subsequently passes through the wall takes 4d10+3 magic damage.',
  },

  /**
   * Book of Sitil (CODEX L2)
   * Three spells: Adjust Appearance, Parallela, Illusion
   */
  'book-of-sitil': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 2,
    spells: [
      {
        id: 'adjust-appearance',
        name: 'Adjust Appearance',
        icon: '🎭',
        shortDescription: 'Magically shift your appearance and clothing',
        isUtility: true,
        successMessage: 'Appearance adjusted! You avoid recognition.',
      },
      {
        id: 'parallela',
        name: 'Parallela',
        icon: '👥',
        shortDescription: 'Spend 2 Hope → next attack hits additional target',
        cost: { type: 'hope', amount: 2 },
        isUtility: true,
        successMessage: 'Parallela! Next attack can hit an additional target.',
      },
      {
        id: 'illusion',
        name: 'Illusion',
        icon: '✨',
        shortDescription: 'Spellcast DC 14 → create visual illusion within Close range',
        triggersPendingSpellAttack: {
          difficulty: 14,
          damage: null, // no damage
        },
        successMessage: 'Illusion created! Holds up until observer is in Melee range.',
      },
    ],
    description: '**Adjust Appearance**: Disguise self | **Parallela**: 2H, next attack hits extra | **Illusion**: DC 14 visual trick',
    fullDescription: '**Adjust Appearance**: You magically shift your appearance and clothing to avoid recognition.\n\n**Parallela**: Spend 2 Hope to cast this spell on yourself or an ally within Close range. The next time the target makes an attack, they can hit an additional target within range that their attack roll would succeed against. You can only hold this spell on one creature at a time.\n\n**Illusion**: Make a Spellcast Roll (14). On a success, create a temporary visual illusion no larger than you within Close range that lasts for as long as you look at it. It holds up to scrutiny until an observer is within Melee range.',
  },

  /**
   * Book of Korvax (CODEX L3)
   * Three spells: Levitation, Recant, Rune Circle
   */
  'book-of-korvax': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 3,
    spells: [
      {
        id: 'levitation',
        name: 'Levitation',
        icon: '🪶',
        shortDescription: 'Spellcast → lift target and move within Close range',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: null, // no damage
        },
        successMessage: 'Levitation! Target lifted into the air.',
      },
      {
        id: 'recant',
        name: 'Recant',
        icon: '🧠',
        shortDescription: 'Spend Hope → target forgets last minute (Reaction 15)',
        cost: { type: 'hope', amount: 1 },
        isUtility: true,
        successMessage: 'Recant! Target must make Reaction Roll (15) or forget.',
      },
      {
        id: 'rune-circle',
        name: 'Rune Circle',
        icon: '⭕',
        shortDescription: 'Mark Stress → create magic circle on the ground',
        cost: { type: 'stress', amount: 1 },
        isUtility: true,
        triggersPendingSpellAttack: {
          difficulty: null,
          damage: '2d12+4',
          damageType: 'magic',
        },
        successMessage: 'Rune Circle created! Damages adversaries in Melee range.',
        summonState: {
          resourceKey: 'rune-circle-active',
          resourceName: 'Rune Circle',
          commandName: 'Deal Damage',
          commandIcon: '⭕',
          commandDescription: 'Melee adversaries take 2d12+4 magic damage, knocked to Very Close',
          triggersPendingSpellAttack: {
            difficulty: null,
            damage: '2d12+4',
            damageType: 'magic',
          },
          dismissName: 'End Circle',
          dismissIcon: '💨',
          dismissMessage: 'Rune Circle fades.',
        },
      },
    ],
    description: '**Levitation**: Lift target | **Recant**: 1H, target forgets | **Rune Circle**: 1S, create → Deal 2d12+4 magic',
    fullDescription: '**Levitation**: Make a Spellcast Roll to temporarily lift a target you can see up into the air and move them within Close range of their original position.\n\n**Recant**: Spend a Hope to force a target within Melee range to make a Reaction Roll (15). On a failure, they forget the last minute of your conversation.\n\n**Rune Circle**: Mark a Stress to create a temporary magical circle on the ground where you stand. All adversaries within Melee range, or who enter Melee range, take 2d12+4 magic damage and are knocked back to Very Close range.',
  },

  /**
   * Book of Exota (CODEX L4)
   * Two spells: Repudiate, Create Construct (with summon toggle)
   */
  'book-of-exota': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 4,
    spells: [
      {
        id: 'repudiate',
        name: 'Repudiate',
        icon: '🚫',
        shortDescription: 'Once/rest: Interrupt magic with Spellcast reaction',
        oncePerRest: true,
        refreshOn: 'short-rest',
        isUtility: true,
        successMessage: 'Repudiate! Magical effect interrupted.',
      },
      {
        id: 'create-construct',
        name: 'Create Construct',
        icon: '🤖',
        shortDescription: 'Spend Hope → animate construct that obeys commands',
        cost: { type: 'hope', amount: 1 },
        isUtility: true,
        triggersPendingSpellAttack: {
          difficulty: null,
          damage: '2d10+3',
          damageType: 'physical',
        },
        successMessage: 'Construct created! Command it with Spellcast Rolls.',
        // When summoned, button transforms to Command/Dismiss split
        summonState: {
          resourceKey: 'construct-active',
          resourceName: 'Construct',
          commandName: 'Command Construct',
          commandIcon: '⚙️',
          commandDescription: 'Spellcast Roll to command; attacks deal 2d10+3 physical',
          triggersPendingSpellAttack: {
            difficulty: null,
            damage: '2d10+3',
            damageType: 'physical',
          },
          dismissName: 'Dismiss',
          dismissIcon: '💨',
          dismissMessage: 'Construct dismissed.',
        },
      },
    ],
    description: '**Repudiate**: Counter magic (1/rest) | **Create Construct**: 1H, summon → Command (2d10+3 physical)',
    fullDescription: '**Repudiate**: You can interrupt a magical effect taking place. Make a reaction roll using your Spellcast trait. Once per rest on a success, the effect stops and any consequences are avoided.\n\n**Create Construct**: Spend a Hope to choose a group of objects around you and create an animated construct from them that obeys basic commands. Make a Spellcast Roll to command them to take action. When necessary, they share your Evasion and traits and their attacks deal 2d10+3 physical damage. You can only maintain one construct at a time, and they fall apart when they take any amount of damage.',
  },

  /**
   * Book of Homet (CODEX L7)
   * Two spells: Pass Through, Plane Gate
   */
  'book-of-homet': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 7,
    spells: [
      {
        id: 'pass-through',
        name: 'Pass Through',
        icon: '🚪',
        shortDescription: 'Once/rest DC 13 → phase through wall with allies',
        oncePerRest: true,
        refreshOn: 'short-rest',
        triggersPendingSpellAttack: {
          difficulty: 13,
          damage: null,
        },
        successMessage: 'Pass Through! You and touching allies phase through.',
      },
      {
        id: 'plane-gate',
        name: 'Plane Gate',
        icon: '🌀',
        shortDescription: 'Once/long rest DC 14 → open portal to another plane',
        oncePerRest: true,
        refreshOn: 'long-rest',
        triggersPendingSpellAttack: {
          difficulty: 14,
          damage: null,
        },
        successMessage: 'Plane Gate! Portal to another dimension opened until rest.',
      },
    ],
    description: '**Pass Through**: DC 13 phase (1/rest) | **Plane Gate**: DC 14 portal (1/long rest)',
    fullDescription: '**Pass Through**: Make a Spellcast Roll (13). Once per rest on a success, you and all creatures touching you can pass through a wall or door within Close range. The effect ends once everyone is on the other side.\n\n**Plane Gate**: Make a Spellcast Roll (14). Once per long rest on a success, open a gateway to a location in another dimension or plane of existence you\'ve been to before. This gateway lasts until your next rest.',
  },

  /**
   * Book of Vyola (CODEX L8)
   * Two spells: Memory Delve, Shared Clarity
   */
  'book-of-vyola': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 8,
    spells: [
      {
        id: 'memory-delve',
        name: 'Memory Delve',
        icon: '🧠',
        shortDescription: 'Spellcast vs Far target → read their memories',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: null,
        },
        successMessage: 'Memory Delve! Ask the GM about target\'s memories.',
      },
      {
        id: 'shared-clarity',
        name: 'Shared Clarity',
        icon: '🔗',
        shortDescription: 'Once/long rest + 1 Hope → link two allies, share Stress',
        cost: { type: 'hope', amount: 1 },
        oncePerRest: true,
        refreshOn: 'long-rest',
        isUtility: true,
        successMessage: 'Shared Clarity! Linked allies can share Stress until rest.',
      },
    ],
    description: '**Memory Delve**: Read target memories | **Shared Clarity**: 1H, link stress (1/long rest)',
    fullDescription: '**Memory Delve**: Make a Spellcast Roll against a target within Far range. On a success, peer into the target\'s mind and ask the GM a question. The GM describes any memories the target has pertaining to the answer.\n\n**Shared Clarity**: Once per long rest, spend a Hope to choose two willing creatures. When one of them would mark Stress, they can choose between the two of them who marks it. This spell lasts until their next rest.',
  },

  /**
   * Book of Ronin (CODEX L9)
   * Two spells: Transform, Eternal Enervation
   */
  'book-of-ronin': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 9,
    spells: [
      {
        id: 'transform',
        name: 'Transform',
        icon: '🪨',
        shortDescription: 'Spellcast DC 15 → become inanimate object',
        triggersPendingSpellAttack: {
          difficulty: 15,
          damage: null,
        },
        successMessage: 'Transform! You become an object until damaged.',
      },
      {
        id: 'eternal-enervation',
        name: 'Eternal Enervation',
        icon: '💀',
        shortDescription: 'Once/long rest → target permanently Vulnerable',
        oncePerRest: true,
        refreshOn: 'long-rest',
        triggersPendingSpellAttack: {
          difficulty: null, // vs target
          damage: null,
        },
        successMessage: 'Eternal Enervation! Target is permanently Vulnerable.',
      },
    ],
    description: '**Transform**: DC 15 become object | **Eternal Enervation**: Permanent Vulnerable (1/long rest)',
    fullDescription: '**Transform**: Make a Spellcast Roll (15). On a success, transform into an inanimate object no larger than twice your normal size. You can remain in this shape until you take damage.\n\n**Eternal Enervation**: Once per long rest, make a Spellcast Roll against a target within Close range. On a success, they become permanently Vulnerable. They can\'t clear this condition by any means.',
  },

  /**
   * Book of Yarrow (CODEX L10)
   * Two spells: Timejammer, Magic Immunity
   */
  'book-of-yarrow': {
    pattern: PATTERN_TYPES.GRIMOIRE,
    cardType: 'Grimoire',
    domain: 'codex',
    level: 10,
    spells: [
      {
        id: 'timejammer',
        name: 'Timejammer',
        icon: '⏰',
        shortDescription: 'Spellcast DC 18 → stop time for everyone but you',
        triggersPendingSpellAttack: {
          difficulty: 18,
          damage: null,
        },
        successMessage: 'Timejammer! Time stops until you target a creature.',
      },
      {
        id: 'magic-immunity',
        name: 'Magic Immunity',
        icon: '✨',
        shortDescription: 'Spend 5 Hope → immune to magic damage until rest',
        cost: { type: 'hope', amount: 5 },
        isUtility: true,
        successMessage: 'Magic Immunity! You are immune to magic damage until rest.',
      },
    ],
    description: '**Timejammer**: DC 18 stop time | **Magic Immunity**: 5H, immune to magic',
    fullDescription: '**Timejammer**: Make a Spellcast Roll (18). On a success, time temporarily slows to a halt for everyone within Far range except for you. It resumes the next time you make an action roll that targets another creature.\n\n**Magic Immunity**: Spend 5 Hope to become immune to magic damage until your next rest.',
  },

  // ---------------------------------------------------------------------------
  // Group C1: Conditional Bonuses (Damage Modifiers)
  // Issue #4 - Implemented Jan 21, 2026
  // ---------------------------------------------------------------------------

  /**
   * Bolt Beacon (SPLENDOR L1)
   * SRD: Make a Spellcast Roll against a target within Far range. On a success,
   * spend a Hope to send a bolt of shimmering light toward them, dealing d8+2
   * magic damage using your Proficiency. The target becomes temporarily Vulnerable
   * and glows brightly until this condition is cleared.
   */
  'bolt-beacon': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 1,
    cost: { type: 'hope', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: 'd8+2',
      damageType: 'magic',
      usesProficiency: true,
      spellName: 'Bolt Beacon',
      successMessage: '✨ Bolt Beacon hit! Target is Vulnerable and glowing brightly!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '✨ Bolt Beacon (1H)',
    },
    description: 'Spellcast vs Far target → d8+2 magic damage. Target becomes Vulnerable and glows.',
  },

  /**
   * Glancing Blow (BLADE L7)
   * SRD: When you fail an attack, you can mark a Stress to deal weapon damage
   * using half your Proficiency.
   */
  'glancing-blow': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'blade',
    level: 7,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '⚔️ Glancing Blow (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '⚔️',
    description: 'On FAILED attack: Mark Stress to deal weapon damage using HALF your Proficiency.',
  },

  /**
   * Battle Monster (BLADE L10)
   * SRD: When you make a successful attack against an adversary, you can mark 4
   * Stress to force the target to mark a number of Hit Points equal to the number
   * of Hit Points you currently have marked instead of rolling for damage.
   */
  'battle-monster': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 10,
    cost: { type: 'stress', amount: 4 },
    buttonLabel: {
      default: '💀 Battle Monster (4S)',
      disabled: { noStress: 'Need 4 Stress capacity' },
    },
    buttonIcon: '💀',
    description: 'On successful attack: Mark 4 Stress → target marks HP equal to YOUR current marked HP!',
  },

  /**
   * Breaking Blow (BONE L8)
   * SRD: When you make a successful attack, you can mark a Stress to make the
   * next successful attack against that same target deal an extra 2d12 damage.
   */
  'breaking-blow': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 8,
    cost: { type: 'stress', amount: 1 },
    sessionStateKey: 'breakingBlowActive',
    buttonLabel: {
      default: '💥 Breaking Blow (1S)',
      active: '💥 +2d12 Ready!',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '💥',
    description: 'After successful attack: Mark Stress. NEXT attack vs same target deals +2d12 damage!',
  },

  /**
   * Not Good Enough (BLADE L1)
   * SRD: When you roll your damage dice, you can reroll any 1s or 2s.
   */
  'not-good-enough': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Passive',
    domain: 'blade',
    level: 1,
    // No cost - passive reminder
    buttonLabel: {
      default: '🎲 Reroll 1s/2s',
    },
    buttonIcon: '🎲',
    description: 'When rolling damage: Reroll any 1s or 2s. FREE - no cost!',
  },

  /**
   * Cruel Precision (BONE L7)
   * SRD: When you make a successful attack with a weapon, gain a bonus to your
   * damage roll equal to either your Finesse or Agility.
   */
  'cruel-precision': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Ability',
    domain: 'bone',
    level: 7,
    // No cost - choose which stat to add
    primaryAction: {
      id: 'finesse',
      label: '🎯 +Finesse',
      description: 'Add your Finesse trait to the damage roll.',
    },
    secondaryAction: {
      id: 'agility',
      label: '⚡ +Agility',
      description: 'Add your Agility trait to the damage roll.',
    },
    description: 'On successful weapon attack: Add Finesse OR Agility to damage. Choose one!',
  },

  /**
   * Gore and Glory (BLADE L9)
   * SRD: When you critically succeed on a weapon attack, gain an additional Hope
   * or clear an additional Stress. Additionally, when you deal enough damage to
   * defeat an enemy, gain a Hope or clear a Stress.
   */
  'gore-and-glory': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Triggered',
    domain: 'blade',
    level: 9,
    // No cost - triggered on crit or defeat
    primaryAction: {
      id: 'hope',
      label: '✨ Gain Hope',
      handler: 'grantHope',
      description: 'Gain 1 Hope.',
    },
    secondaryAction: {
      id: 'stress',
      label: '💆 Clear Stress',
      handler: 'clearStress',
      description: 'Clear 1 Stress.',
    },
    description: 'On CRIT or defeating enemy: Gain Hope OR clear Stress. Your choice!',
  },

  /**
   * Rage Up (BLADE L6)
   * SRD: Before you make an attack, you can mark a Stress to gain a bonus to your
   * damage roll equal to twice your Strength. You can Rage Up twice per attack.
   */
  'rage-up': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Ability',
    domain: 'blade',
    level: 6,
    primaryAction: {
      id: 'single',
      label: '💢 Rage (1S)',
      cost: { type: 'stress', amount: 1 },
      description: 'Mark 1 Stress for +2× Strength to damage roll.',
    },
    secondaryAction: {
      id: 'double',
      label: '🔥 Double (2S)',
      cost: { type: 'stress', amount: 2 },
      description: 'Mark 2 Stress for +4× Strength to damage roll (max per attack).',
    },
    description: 'Before attack: Mark 1-2 Stress for +2× Strength per Stress to damage!',
  },

  /**
   * Forceful Push (VALOR L1)
   * SRD: Make an attack with your primary weapon against a target within Melee range.
   * On a success, you deal damage and knock them back to Close range. On a success
   * with Hope, add a d6 to your damage roll. Additionally, you can spend a Hope
   * to make them temporarily Vulnerable.
   */
  'forceful-push': {
    pattern: PATTERN_TYPES.TRIPLE_BUTTON,
    cardType: 'Ability',
    domain: 'valor',
    level: 1,
    actions: {
      attack: {
        label: 'Push',
        icon: '🗡️',
        description: 'Melee attack + knockback to Close range.',
      },
      bonusDamage: {
        label: '+d6 (1H)',
        icon: '🎲',
        cost: { type: 'hope', amount: 1 },
        description: 'On success with Hope: add d6 to damage.',
        addsBonusDice: { count: 1, size: 6 },
      },
      vulnerable: {
        label: 'Vuln (1H)',
        icon: '💢',
        cost: { type: 'hope', amount: 1 },
        description: 'Spend Hope to make target Vulnerable.',
      },
    },
    description: 'Melee attack + knockback. Optional: Spend Hope for +d6 and/or Vulnerable.',
  },

  /**
   * Champion's Edge (BLADE L5)
   * SRD: When you critically succeed on an attack, you can spend up to 3 Hope
   * and choose one of the following options for each Hope spent: You clear a
   * Hit Point. / You clear an Armor Slot. / The target must mark an additional
   * Hit Point. You can't choose the same option more than once.
   */
  'champions-edge': {
    pattern: PATTERN_TYPES.TRIPLE_BUTTON,
    cardType: 'Triggered',
    domain: 'blade',
    level: 5,
    // Triggered on critical hit - spend 1-3 Hope for choices (can't repeat)
    actions: {
      healHp: {
        label: '❤️ Clear HP',
        icon: '❤️',
        cost: { type: 'hope', amount: 1 },
        handler: 'clearHP',
        description: 'Clear 1 Hit Point.',
      },
      clearArmor: {
        label: '🛡️ Clear Armor',
        icon: '🛡️',
        cost: { type: 'hope', amount: 1 },
        handler: 'clearArmor',
        description: 'Clear 1 Armor Slot.',
      },
      extraDamage: {
        label: '💀 +1 HP Mark',
        icon: '💀',
        cost: { type: 'hope', amount: 1 },
        description: 'Target marks +1 additional HP.',
      },
    },
    uniqueSelectionsOnly: true, // Can't pick same option twice
    description: 'On CRIT: Spend 1-3 Hope. Each Hope = one effect (can\'t repeat same choice).',
  },

  // ---------------------------------------------------------------------------
  // Group C2: Reactions (Damage Modifiers)
  // Issue #4 - Implemented Jan 21, 2026
  // Already implemented: Counterspell (ARCANA L3), I See It Coming (BONE L1)
  // ---------------------------------------------------------------------------

  /**
   * Get Back Up (BLADE L1)
   * SRD: When you take Severe damage, mark a Stress to reduce the severity by one threshold.
   */
  'get-back-up': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'blade',
    level: 1,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '💪 Get Back Up (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '💪',
    description: 'On Severe damage: Mark Stress to reduce severity by one threshold.',
  },

  /**
   * Scramble (BLADE L3)
   * SRD: Once per rest, when a creature within Melee range would deal damage to you,
   * avoid the attack and safely move out of Melee range of the enemy.
   */
  'scramble': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'blade',
    level: 3,
    tokenId: 'scramble-charge', // Once per rest
    buttonLabel: {
      default: '🏃 Scramble',
      disabled: { noToken: 'Used This Rest' },
    },
    buttonIcon: '🏃',
    description: 'Once/rest: When Melee enemy deals damage, avoid and move out of range.',
  },

  /**
   * Redirect (BONE L4)
   * SRD: When an attack made against you from beyond Melee range fails,
   * roll Proficiency d6s. If any roll a 6, mark Stress to redirect attack
   * to damage an adversary within Very Close range.
   */
  'redirect': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'bone',
    level: 4,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '🔄 Redirect (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '🔄',
    description: 'On failed ranged attack vs you: Roll Prof×d6. On any 6, redirect to nearby enemy.',
  },

  /**
   * Rapid Riposte (BONE L6)
   * SRD: When an attack made against you from within Melee range fails,
   * mark Stress to seize the opportunity and deal weapon damage to attacker.
   */
  'rapid-riposte': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'bone',
    level: 6,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '⚔️ Riposte (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '⚔️',
    description: 'On failed Melee attack vs you: Mark Stress to deal weapon damage to attacker.',
  },

  /**
   * Shrug It Off (VALOR L7)
   * SRD: When you would take damage, mark Stress to reduce severity by one threshold.
   * Roll d6 - on 3 or lower, card goes to vault.
   */
  'shrug-it-off': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'valor',
    level: 7,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '🛡️ Shrug It Off (1S)',
      disabled: { noStress: 'No Stress' },
    },
    buttonIcon: '🛡️',
    description: 'On damage: Mark Stress to reduce severity. Roll d6 - on ≤3, card vaults.',
  },

  /**
   * Arcane Reflection (ARCANA L8)
   * SRD: When you would take magic damage, spend any number of Hope to roll
   * that many d6s. If any roll a 6, reflect the attack back to the caster.
   */
  'arcane-reflection': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'arcana',
    level: 8,
    buttonLabel: {
      default: '✨ Reflect Magic',
      disabled: { noHope: 'No Hope' },
    },
    buttonIcon: '✨',
    description: 'On magic damage: Spend Hope → roll d6s. Any 6 reflects attack to caster!',
  },

  // ---------------------------------------------------------------------------
  // Group C3: Area Effects (Damage Modifiers)
  // Issue #4 - Implemented Jan 21, 2026
  // ---------------------------------------------------------------------------

  /**
   * Preservation Blast (ARCANA L4)
   * SRD: Make a Spellcast Roll against all targets within Melee range.
   * Targets you succeed against are forced back to Far range and take
   * d8+3 magic damage using your Spellcast trait.
   */
  'preservation-blast': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 4,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: 'd8+3',
      damageType: 'magic',
      isAoE: true,
      usesSpellcast: true, // SRD: "d8+3 magic damage using your Spellcast trait"
      spellName: 'Preservation Blast',
      successMessage: '💨 Blast! Targets pushed to Far range!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '💨 Preservation Blast',
    },
    buttonIcon: '💨',
    description: 'Spellcast vs all Melee targets → d8+3 magic + knockback to Far.',
  },

  /**
   * Ground Pound (VALOR L8)
   * SRD: Spend 2 Hope to strike the ground and make a Strength Roll against
   * all targets within Very Close range. Targets are thrown to Far range and
   * must make Reaction (17) - fail: 4d10+8 damage, success: half damage.
   */
  'ground-pound': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'valor',
    level: 8,
    cost: { type: 'hope', amount: 2 },
    buttonLabel: {
      default: '💥 Ground Pound (2H)',
    },
    buttonIcon: '💥',
    description: 'Spend 2H + Strength Roll vs Very Close → 4d10+8 damage + Far knockback.',
  },

  /**
   * Chain Lightning (ARCANA L5)
   * SRD: Mark 2 Stress + Spellcast Roll → 2d8+4 magic to Close targets.
   * Chains to adjacent targets also within Close range until no more in range.
   */
  'chain-lightning': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 5,
    cost: { type: 'stress', amount: 2 },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: '2d8+4',
      damageType: 'magic',
      isAoE: true,
      spellName: 'Chain Lightning',
      successMessage: '⚡ Chain Lightning! Chains to adjacent targets!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '⚡ Chain Lightning (2S)',
    },
    buttonIcon: '⚡',
    description: 'Mark 2S → 2d8+4 magic to Close. Chains to adjacent targets!',
  },

  /**
   * Falling Sky (ARCANA L10)
   * SRD: Make a Spellcast Roll against all adversaries within Far range.
   * Mark any number of Stress to make shards rain down. Targets take
   * 1d20+2 magic damage for each Stress marked.
   */
  'falling-sky': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 10,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: '1d20+2', // Per stress marked
      damageType: 'magic',
      isAoE: true,
      spellName: 'Falling Sky',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '☄️ Falling Sky',
    },
    buttonIcon: '☄️',
    description: 'Spellcast vs Far targets. Mark Stress → 1d20+2 per Stress!',
  },

  /**
   * Earthquake (ARCANA L9)
   * SRD: Spellcast (16), once per rest. All targets within Very Far range who
   * aren't flying must make Reaction (18). Fail: 3d10+8 physical + Vulnerable.
   * Success: half damage. Terrain becomes difficult.
   */
  'earthquake': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 9,
    tokenId: 'earthquake-charge', // Once per rest
    triggersPendingSpellAttack: {
      difficulty: 16,
      damage: '3d10+8',
      damageType: 'physical',
      isAoE: true,
      spellName: 'Earthquake',
      successMessage: '🌍 Earthquake! Failed targets are Vulnerable!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌍 Earthquake (DC 16)',
      disabled: { noToken: 'Used This Rest' },
    },
    description: 'Once/rest: DC 16 → 3d10+8 physical to Very Far (non-flying). Failed = Vulnerable.',
  },

  /**
   * Stunning Sunlight (SPLENDOR L8)
   * SRD: Spellcast Roll to unleash burning sunlight against Far adversaries.
   * Spend Hope to force targets to Reaction (14). Fail: 4d20+5 + Stunned.
   * Success: 3d20+3 magic damage.
   */
  'stunning-sunlight': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 8,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: '4d20+5', // Full on failed reaction
      damageType: 'magic',
      isAoE: true,
      spellName: 'Stunning Sunlight',
      successMessage: '☀️ Stunning Sunlight! Failed targets are Stunned!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '☀️ Stunning Sunlight',
    },
    buttonIcon: '☀️',
    description: 'Spellcast + spend Hope per target → 4d20+5 (fail) or 3d20+3 (pass). Fail = Stunned.',
  },

  /**
   * Tempest (SAGE L10)
   * SRD: Choose Blizzard, Hurricane, or Sandstorm. Spellcast vs Far targets.
   * Effect persists until GM spends Fear to end.
   * Blizzard: 2d20+8 + Vulnerable
   * Hurricane: 3d10+10 + wind restriction
   * Sandstorm: 5d6+9 + ranged disadvantage
   */
  'tempest': {
    pattern: PATTERN_TYPES.TRIPLE_BUTTON,
    cardType: 'Spell',
    domain: 'sage',
    level: 10,
    actions: {
      blizzard: {
        label: '❄️ Blizzard',
        icon: '❄️',
        description: '2d20+8 magic + targets Vulnerable',
      },
      hurricane: {
        label: '🌀 Hurricane',
        icon: '🌀',
        description: '3d10+10 magic + wind restriction',
      },
      sandstorm: {
        label: '🏜️ Sandstorm',
        icon: '🏜️',
        description: '5d6+9 magic + ranged disadvantage',
      },
    },
    description: 'Choose tempest type → AoE damage + persistent effect until GM spends Fear.',
  },

  /**
   * Disintegration Wave (CODEX L9)
   * SRD: Spellcast (18), once per long rest. GM reveals targets ≤18 Difficulty.
   * Mark Stress per target → instant permanent death (cannot be revived).
   */
  'disintegration-wave': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'codex',
    level: 9,
    tokenId: 'disintegration-wave-charge', // Once per long rest
    triggersPendingSpellAttack: {
      difficulty: 18,
      damage: null, // Instant death, no damage roll
      isAoE: true,
      spellName: 'Disintegration Wave',
      successMessage: '💀 Disintegration Wave! Targets with Difficulty ≤18 are killed permanently.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '💀 Disintegration Wave (DC 18)',
      disabled: { noToken: 'Used This Long Rest' },
    },
    description: 'Once/long rest: DC 18. Mark Stress per target → instant permanent death.',
  },

  // ---------------------------------------------------------------------------
  // PARTY BUFF PATTERN CARDS (Group F)
  // ---------------------------------------------------------------------------

  /**
   * Battle Cry (BLADE L8)
   * SRD: Once per long rest, all allies clear Stress, gain Hope, and have
   * advantage on their next roll.
   */
  'battle-cry': {
    pattern: PATTERN_TYPES.PARTY_BUFF,
    cardType: 'Ability',
    domain: 'blade',
    level: 8,
    tokenId: 'battle-cry-charge', // Once per long rest tracking
    action: {
      label: '⚔️ Rally the Party!',
      broadcastType: 'battleCry',
      variant: 'attack',
      icon: '⚔️',
      soloLabel: 'Join Campaign First',
      usedLabel: 'Battle Cry Used',
      restoreMessage: 'Restores after a long rest.',
      successMessage: 'Battle Cry! Party empowered!',
    },
    description: 'Once per long rest: All allies clear Stress, gain Hope, advantage until fail.',
  },

  /**
   * Healing Strike (SPLENDOR L7)
   * SRD: When you deal damage to an adversary, you can spend 2 Hope to
   * clear a Hit Point on an ally within Close range.
   */
  'healing-strike': {
    pattern: PATTERN_TYPES.ALLY_TARGET,
    cardType: 'Spell',
    domain: 'splendor',
    level: 7,
    action: {
      label: '💚 Heal Ally (2H)',
      variant: 'healing',
      cost: { type: 'hope', amount: 2 },
      actionType: 'healAlly',
      healAmount: 1,
      showVitals: true,
      filter: (ally) => ally.hp < ally.maxHp, // Only show damaged allies
      description: 'On dealing damage: Spend 2 Hope to heal ally 1 HP.',
    },
    description: 'When you deal damage: Spend 2 Hope to heal an ally within Close range.',
  },

  /**
   * Reassurance (SPLENDOR L1)
   * SRD: Once per rest, after an ally attempts an action roll but before
   * the consequences take place, you can offer assistance or words of support.
   * When you do, your ally can reroll their dice.
   */
  'reassurance': {
    pattern: PATTERN_TYPES.ALLY_TARGET,
    cardType: 'Ability',
    domain: 'splendor',
    level: 1,
    tokenId: 'reassurance-charge', // Once per rest tracking
    action: {
      label: '✨ Offer Reroll',
      variant: 'support',
      actionType: 'grantReroll',
      showVitals: false,
      usedLabel: 'Used This Rest',
      restoreMessage: 'Restores after a rest.',
      description: 'Grant ally a reroll on their failed action.',
    },
    description: 'Once per rest: After ally fails action roll, let them reroll.',
  },

  /**
   * Share the Burden (GRACE L6)
   * SRD: Once per rest, take on the Stress from a willing creature within
   * Melee range. Transfer any number of their Stress to you, then gain a
   * Hope for each Stress transferred.
   */
  'share-the-burden': {
    pattern: PATTERN_TYPES.ALLY_TARGET,
    cardType: 'Spell',
    domain: 'grace',
    level: 6,
    tokenId: 'share-burden-charge', // Once per rest tracking
    action: {
      label: '💜 Take Stress',
      variant: 'support',
      actionType: 'transferStress',
      showVitals: true,
      filter: (ally) => ally.stress > 0, // Only show stressed allies
      usedLabel: 'Used This Rest',
      restoreMessage: 'Restores after a rest.',
      description: 'Transfer ally\'s Stress to yourself, gain Hope per Stress.',
    },
    description: 'Once per rest: Transfer ally\'s Stress to yourself. Gain 1 Hope per Stress.',
  },

  // ---------------------------------------------------------------------------
  // CRIT TRIGGER PATTERN CARDS
  // ---------------------------------------------------------------------------

  /**
   * Critical Inspiration (VALOR L3)
   * SRD: Once per rest, when you critically succeed on an attack, all allies
   * within Very Close range can clear a Stress or gain a Hope.
   */
  'critical-inspiration': {
    pattern: PATTERN_TYPES.CRIT_TRIGGER,
    cardType: 'Ability',
    domain: 'valor',
    level: 3,
    tokenId: 'crit-inspiration-charge', // Once per rest tracking
    action: {
      icon: '✨',
      effects: [
        {
          type: 'clearStress',
          label: '💙 Clear Stress',
          variant: 'support',
        },
        {
          type: 'grantHope',
          label: '✨ Grant Hope',
          variant: 'support',
        },
      ],
      stressAmount: 1,
      hopeAmount: 1,
      includeSelf: false, // Allies only, not self
      restoreMessage: 'Restores after a rest.',
    },
    description: 'On CRIT: Allies clear 1 Stress OR gain 1 Hope (choose one for all).',
  },

  /**
   * Rousing Strike (VALOR L5)
   * SRD: Once per rest, when you critically succeed on an attack, you and
   * all allies who can see or hear you can clear a Hit Point or 1d4 Stress.
   */
  'rousing-strike': {
    pattern: PATTERN_TYPES.CRIT_TRIGGER,
    cardType: 'Ability',
    domain: 'valor',
    level: 5,
    tokenId: 'rousing-strike-charge', // Once per rest tracking
    action: {
      icon: '⚔️',
      effects: [
        {
          type: 'clearHP',
          label: '💚 Clear 1 HP',
          variant: 'healing',
        },
        {
          type: 'clearStress',
          label: '💙 Clear 1d4 Stress',
          variant: 'support',
        },
      ],
      healAmount: 1,
      rollStress: true, // Roll 1d4 for stress clearing
      includeSelf: true, // Includes self
      restoreMessage: 'Restores after a rest.',
    },
    description: 'On CRIT: You + allies clear 1 HP OR 1d4 Stress (choose one for all).',
  },

  // ---------------------------------------------------------------------------
  // LIFE WARD PATTERN CARDS
  // ---------------------------------------------------------------------------

  /**
   * Life Ward (SPLENDOR L4)
   * SRD: Spend 3 Hope and choose an ally within Close range. They are marked
   * with a glowing sigil of protection. When this ally would make a death move,
   * they clear a Hit Point instead.
   */
  'life-ward': {
    pattern: PATTERN_TYPES.LIFE_WARD,
    cardType: 'Spell',
    domain: 'splendor',
    level: 4,
    action: {
      label: '🛡️ Cast Life Ward (3H)',
      variant: 'support',
      cost: { type: 'hope', amount: 3 },
      description: 'Ward ally - they clear 1 HP instead of making a death move.',
    },
    description: 'Spend 3 Hope: Ward ally. They clear 1 HP instead of death move (ends on use/recast/rest).',
  },

  /**
   * Shield Aura (SPLENDOR L8)
   * SRD: Mark a Stress to cast a protective aura on a target within Very Close range.
   * When the target marks an Armor Slot, they reduce the severity of the attack by
   * an additional threshold. If this spell causes a creature who would be damaged
   * to instead mark no Hit Points, the effect ends.
   */
  'shield-aura': {
    pattern: PATTERN_TYPES.ALLY_TARGET,
    cardType: 'Spell',
    domain: 'splendor',
    level: 8,
    action: {
      label: '✨ Cast Shield Aura (1S)',
      variant: 'support',
      cost: { type: 'stress', amount: 1 },
      actionType: 'grantShieldAura',
      armorAmount: 1, // Extra threshold reduction
      showVitals: true,
      description: 'Ally gets extra armor threshold reduction. Ends when fully negating damage.',
    },
    description: 'Mark 1 Stress: Ally armor reduces damage by extra threshold (ends on full negate/recast).',
  },

  // ---------------------------------------------------------------------------
  // Domain-Touched Pattern Cards
  // All require 4+ cards from the same domain in loadout to activate
  // ---------------------------------------------------------------------------

  /**
   * Arcana-Touched (ARCANA L7)
   * SRD: When 4+ domain cards in loadout are from Arcana domain, gain:
   * - +1 bonus to Spellcast Rolls
   * - Once per rest: Switch Hope and Fear dice results after a roll
   */
  'arcana-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'ARCANA',
    level: 7,
    passiveBonus: {
      type: 'spellcast',
      value: 1,
      description: '+1 to Spellcast Rolls',
    },
    abilityAction: {
      tokenId: 'arcana-touched-swap',
      refreshTrigger: 'rest',
      label: 'Swap Hope/Fear',
      variant: 'primary',
      hopeCost: 0,
      description: 'Switch the results of your Hope and Fear dice after a roll',
      sessionStateKey: 'arcanaTouchedSwapAvailable',
    },
    description: 'When 4+ Arcana cards in loadout: +1 Spellcast. Once/rest: Swap Hope and Fear dice results.',
    tokenConfig: {
      id: 'arcana-touched-switch',
      name: 'Arcana-Touched (Dice Switch)',
      max: 1,
      refreshOn: 'rest',
      requiresActivation: true,
    },
  },

  /**
   * Blade-Touched (BLADE L7)
   * SRD: When 4+ domain cards in loadout are from Blade domain, gain:
   * - +2 bonus to attack rolls
   * - +4 to Severe damage threshold
   * (Passive only - no button needed)
   */
  'blade-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'BLADE',
    level: 7,
    passiveBonus: {
      type: 'multi',
      bonuses: [
        { stat: 'attackRoll', value: 2, description: '+2 to attack rolls' },
        { stat: 'severeThreshold', value: 4, description: '+4 to Severe threshold' },
      ],
    },
    abilityAction: null, // Passive only - no once-per-rest ability
    description: 'When 4+ Blade cards in loadout: +2 attack rolls, +4 Severe threshold.',
  },

  /**
   * Bone-Touched (BONE L7)
   * SRD: When 4+ domain cards in loadout are from Bone domain, gain:
   * - +1 bonus to Agility
   * - Once per rest: Spend 3 Hope to cause a successful attack against you to fail instead
   */
  'bone-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'BONE',
    level: 7,
    passiveBonus: {
      type: 'trait',
      stat: 'Agility',
      value: 1,
      description: '+1 to Agility',
    },
    abilityAction: {
      tokenId: 'bone-touched-negate',
      refreshTrigger: 'rest',
      label: 'Negate Attack (3H)',
      variant: 'warning',
      hopeCost: 3,
      description: 'Spend 3 Hope to cause an attack that succeeded against you to fail instead',
    },
    description: 'When 4+ Bone cards in loadout: +1 Agility. Once/rest: Spend 3 Hope to negate a hit.',
  },

  /**
   * Codex-Touched (CODEX L7)
   * SRD: When 4+ domain cards in loadout are from Codex domain, gain:
   * - Mark Stress to add Proficiency to a Spellcast Roll
   * - Once per rest: Replace this card with any card from vault without paying Recall Cost
   */
  'codex-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'CODEX',
    level: 7,
    passiveBonus: {
      type: 'conditional-spellcast',
      stressCost: 1,
      addProficiency: true,
      description: 'Mark Stress to add Proficiency to Spellcast Roll',
    },
    abilityAction: {
      tokenId: 'codex-touched-swap',
      refreshTrigger: 'rest',
      label: 'Free Card Swap',
      variant: 'secondary',
      hopeCost: 0,
      description: 'Replace this card with any vault card without paying Recall Cost',
      requiresCardSwap: true, // Special flag for UI to show card selector
    },
    description: 'When 4+ Codex cards in loadout: Stress → +Prof to Spellcast. Once/rest: Free card swap.',
  },

  /**
   * Grace-Touched (GRACE L7)
   * SRD: When 4+ domain cards in loadout are from Grace domain, gain:
   * - Mark Armor Slot instead of Stress
   * - When forcing target to mark HP: choose to mark Stress instead
   */
  'grace-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'GRACE',
    level: 7,
    passiveBonus: {
      type: 'armor-stress-swap',
      description: 'May mark Armor Slot instead of Stress',
    },
    abilityAction: {
      type: 'trigger', // Not a button - triggers on dealing damage
      trigger: 'force-hp-mark',
      effect: 'convert-to-stress',
      description: 'When forcing target to mark HP, may convert to Stress instead',
    },
    description: 'When 4+ Grace cards in loadout: Armor Slot = Stress. HP damage → Stress option.',
  },

  /**
   * Midnight-Touched (MIDNIGHT L7)
   * SRD: When 4+ domain cards in loadout are from Midnight domain, gain:
   * - Once per rest: When at 0 Hope and GM gains Fear, gain Hope instead
   * - On successful attack: Mark Stress to add Fear Die result to damage
   */
  'midnight-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'MIDNIGHT',
    level: 7,
    passiveBonus: {
      type: 'conditional-hope',
      condition: 'at-zero-hope',
      oncePerRest: true,
      description: 'At 0 Hope: GM Fear → your Hope (once/rest)',
    },
    abilityAction: {
      type: 'damage-boost',
      stressCost: 1,
      label: 'Add Fear Die (1S)',
      variant: 'attack',
      description: 'On successful attack: Mark Stress to add Fear Die to damage',
      sessionStateKey: 'midnightFearDamageArmed',
    },
    description: 'When 4+ Midnight cards in loadout: 0 Hope → steal GM Fear. Stress → +Fear die damage.',
    tokenConfig: [
      {
        id: 'midnight-hope-steal',
        name: 'Midnight-Touched (Hope Steal)',
        max: 1,
        refreshOn: 'rest',
        requiresActivation: true,
      },
      {
        id: 'midnight-fear-damage',
        name: 'Midnight-Touched (Fear Damage)',
        max: 99,
        refreshOn: 'manual',
        requiresActivation: true,
        cost: { type: 'stress', amount: 1 },
      },
    ],
  },

  /**
   * Sage-Touched (SAGE L7)
   * SRD: When 4+ domain cards in loadout are from Sage domain, gain:
   * - +2 bonus to Spellcast Rolls in natural environments
   * - Once per rest: Double Agility or Instinct for one roll (declare before rolling)
   */
  'sage-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'SAGE',
    level: 7,
    passiveBonus: {
      type: 'conditional-spellcast',
      value: 2,
      condition: 'natural-environment',
      description: '+2 to Spellcast in natural environments',
    },
    abilityAction: {
      tokenId: 'sage-touched-double',
      refreshTrigger: 'rest',
      label: 'Double Trait',
      variant: 'primary',
      hopeCost: 0,
      traitOptions: ['Agility', 'Instinct'],
      description: 'Double Agility or Instinct for one roll (declare before rolling)',
      sessionStateKey: 'sageTouchedDoubleArmed',
    },
    description: 'When 4+ Sage cards in loadout: +2 Spellcast (nature). Once/rest: Double Agility/Instinct.',
    tokenConfig: {
      id: 'sage-touched-double-trait',
      name: 'Sage-Touched (Double Trait)',
      max: 1,
      refreshOn: 'rest',
      requiresActivation: true,
    },
  },

  /**
   * Splendor-Touched (SPLENDOR L7)
   * SRD: When 4+ domain cards in loadout are from Splendor domain, gain:
   * - +3 bonus to Severe damage threshold
   * - Once per long rest: Convert incoming HP damage to equal Stress or Hope spend
   */
  'splendor-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'SPLENDOR',
    level: 7,
    passiveBonus: {
      type: 'threshold',
      stat: 'severeThreshold',
      value: 3,
      description: '+3 to Severe damage threshold',
    },
    abilityAction: {
      tokenId: 'splendor-touched-convert',
      refreshTrigger: 'long-rest', // Note: long rest, not regular rest
      label: 'Convert HP Damage',
      variant: 'support',
      hopeCost: 0,
      description: 'Convert incoming HP to equal Stress or Hope spend',
      conversionOptions: ['stress', 'hope'],
    },
    description: 'When 4+ Splendor cards in loadout: +3 Severe threshold. Once/long rest: HP → Stress/Hope.',
  },

  /**
   * Valor-Touched (VALOR L7)
   * SRD: When 4+ domain cards in loadout are from Valor domain, gain:
   * - +1 bonus to Armor Score
   * - When marking HP without using Armor Slot, clear an Armor Slot
   */
  'valor-touched': {
    pattern: PATTERN_TYPES.DOMAIN_TOUCHED,
    cardType: 'Ability',
    domain: 'VALOR',
    level: 7,
    passiveBonus: {
      type: 'armorScore',
      value: 1,
      description: '+1 to Armor Score',
    },
    abilityAction: {
      type: 'trigger', // Not a button - triggers automatically
      trigger: 'mark-hp-without-armor',
      effect: 'clear-armor-slot',
      description: 'When marking HP without using Armor Slot, clear an Armor Slot',
    },
    description: 'When 4+ Valor cards in loadout: +1 Armor Score. HP mark → clear Armor Slot.',
  },

  // ---------------------------------------------------------------------------
  // Hope Spend Cards (Group C1 - Issue #4)
  // ---------------------------------------------------------------------------

  // ===== PHASE 1: Simple Hope Spend (9 cards) =====

  /**
   * Deft Deceiver (GRACE L1)
   * SRD: Spend a Hope to gain advantage on a roll to deceive or trick someone
   * into believing a lie you tell them.
   */
  'deft-deceiver': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'grace',
    level: 1,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'deftDeceiverActive',
    buttonLabel: {
      default: '🎭 Deceive (1H)',
      active: '✓ Advantage Ready',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'Spend Hope to gain advantage on deception/trick roll.',
  },

  /**
   * Shape Material (SPLENDOR L5)
   * SRD: Spend a Hope to shape a section of natural material you're touching
   * (such as stone, ice, or wood) to suit your purpose.
   */
  'shape-material': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 5,
    cost: { type: 'hope', amount: 1 },
    buttonLabel: {
      default: '🪨 Shape Material (1H)',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'Spend Hope to shape natural material (stone, ice, wood) within Close range.',
  },

  /**
   * Floating Eye (ARCANA L2)
   * SRD: Spend a Hope to create a single, small floating orb that you can move
   * anywhere within Very Far range. You can see through the orb.
   */
  'floating-eye': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 2,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'floatingEyeActive',
    buttonLabel: {
      default: '👁️ Floating Eye (1H)',
      active: '✓ Eye Active',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'Spend Hope to create scouting orb (Very Far range). Ends if damaged or out of range.',
  },

  /**
   * Endless Charisma (GRACE L7)
   * SRD: After you make an action roll to persuade, lie, or garner favor,
   * you can spend a Hope to reroll the Hope or Fear Die.
   */
  'endless-charisma': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'grace',
    level: 7,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'endlessCharismaReady',
    buttonLabel: {
      default: '✨ Reroll Die (1H)',
      active: '✓ Reroll Ready',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'After persuade/lie/favor roll: Spend Hope to reroll Hope or Fear Die.',
  },

  /**
   * Vanishing Dodge (MIDNIGHT L7)
   * SRD: When an attack that would deal physical damage fails, spend Hope to
   * become Hidden and teleport Close to attacker. Hidden until next action roll.
   */
  'vanishing-dodge': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 7,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'vanishingDodgeHidden',
    buttonLabel: {
      default: '👻 Vanishing Dodge (1H)',
      active: '✓ Hidden!',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'When physical attack fails vs you: Spend Hope to become Hidden + teleport Close to attacker.',
  },

  /**
   * Cloaking Blast (ARCANA L7)
   * SRD: When you make a successful Spellcast Roll to cast a different spell,
   * you can spend a Hope to become Cloaked.
   */
  'cloaking-blast': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 7,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'cloakingBlastActive',
    buttonLabel: {
      default: '🫥 Cloak (1H)',
      active: '✓ Cloaked',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'After successful Spellcast: Spend Hope to become Cloaked (unseen while stationary).',
  },

  /**
   * Whirlwind (BLADE L1)
   * SRD: When you make a successful attack within Very Close range, spend Hope
   * to attack all other targets within Very Close range (half damage).
   */
  'whirlwind': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 1,
    cost: { type: 'hope', amount: 1 },
    sessionStateKey: 'whirlwindReady',
    buttonLabel: {
      default: '🌀 Whirlwind (1H)',
      active: '✓ AoE Ready',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'After successful melee attack: Spend Hope to hit all other Very Close targets (half damage).',
  },

  /**
   * Ferocity (BONE L2)
   * SRD: When you cause an adversary to mark 1+ HP, spend 2 Hope to increase
   * Evasion by HP marked (lasts until next attack against you).
   */
  'ferocity': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 2,
    cost: { type: 'hope', amount: 2 },
    sessionStateKey: 'ferocityBonus',
    buttonLabel: {
      default: '🔥 Ferocity (2H)',
      active: '✓ +Evasion Active',
      disabled: { noHope: 'Need 2 Hope' },
    },
    activeIndicator: {
      color: 'orange',
      message: 'Evasion boosted until next attack against you',
    },
    description: 'After enemy marks HP: Spend 2 Hope for +Evasion = HP marked (until next attack vs you).',
  },

  /**
   * Support Tank (VALOR L4)
   * SRD: When an ally within Close range fails a roll, spend 2 Hope to allow
   * them to reroll either their Hope or Fear Die.
   */
  'support-tank': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'valor',
    level: 4,
    cost: { type: 'hope', amount: 2 },
    buttonLabel: {
      default: '🛡️ Support Tank (2H)',
      disabled: { noHope: 'Need 2 Hope' },
    },
    description: 'When ally in Close range fails roll: Spend 2 Hope to let them reroll Hope or Fear Die.',
  },

  // ===== PHASE 2: Rest-Restricted Cards (4 cards) =====

  /**
   * Divination (SPLENDOR L4)
   * SRD: Once per long rest, spend 3 Hope to ask one "yes or no" question
   * about the near future.
   */
  'divination': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 4,
    cost: { type: 'hope', amount: 3 },
    tokenId: 'divination-charge',
    validation: { checkToken: true },
    buttonLabel: {
      default: '🔮 Divination (3H)',
      disabled: { noToken: 'Used This Rest', noHope: 'Need 3 Hope' },
    },
    description: 'Once per long rest: Spend 3 Hope to ask one yes/no question about the near future.',
  },

  /**
   * Smite (SPLENDOR L5)
   * SRD: Once per rest, spend 3 Hope to charge your powerful smite.
   * Next successful weapon attack deals double damage (magic).
   */
  'smite': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 5,
    cost: { type: 'hope', amount: 3 },
    tokenId: 'smite-charge',
    sessionStateKey: 'smiteCharged',
    validation: { checkToken: true },
    buttonLabel: {
      default: '⚡ Charge Smite (3H)',
      active: '✓ Smite Ready!',
      disabled: { noToken: 'Used This Rest', noHope: 'Need 3 Hope' },
    },
    description: 'Once per rest: Spend 3 Hope to charge smite. Next weapon attack = double damage (magic).',
  },

  /**
   * Battle-Hardened (BLADE L6)
   * SRD: Once per long rest when you would make a Death Move, spend Hope to
   * clear a Hit Point instead.
   */
  'battle-hardened': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 6,
    cost: { type: 'hope', amount: 1 },
    tokenId: 'battle-hardened-charge',
    validation: { checkToken: true },
    buttonLabel: {
      default: '💪 Avoid Death (1H)',
      disabled: { noToken: 'Used This Rest', noHope: 'Need 1 Hope' },
    },
    description: 'Once per long rest: When making Death Move, spend Hope to clear 1 HP instead.',
  },

  /**
   * Recovery (BONE L6)
   * SRD: During a short rest, you can choose a long rest downtime move instead.
   * Spend Hope to let an ally do the same.
   */
  'recovery': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 6,
    cost: { type: 'hope', amount: 1 },
    buttonLabel: {
      default: '🏕️ Ally Long Rest Move (1H)',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'During short rest: You get long rest move. Spend Hope for ally to do same.',
  },

  // ===== PHASE 3: Complex Cards (6 cards) =====

  /**
   * Bold Presence (VALOR L2)
   * SRD: Two effects:
   * 1. Spend Hope to add Strength to Presence Roll
   * 2. Once per rest, avoid gaining a condition
   */
  'bold-presence': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Ability',
    domain: 'valor',
    level: 2,
    primaryAction: {
      id: 'presenceBoost',
      label: '💪 +Str to Presence (1H)',
      cost: { type: 'hope', amount: 1 },
      sessionStateKey: 'boldPresenceActive',
      description: 'Add Strength to a Presence Roll.',
    },
    secondaryAction: {
      id: 'avoidCondition',
      label: '🛡️ Avoid Condition',
      tokenId: 'bold-presence-avoid',
      description: 'Once per rest: Describe how bold presence helps avoid a condition.',
    },
    description: 'Spend Hope to add Str to Presence. Once per rest: avoid gaining a condition.',
  },

  /**
   * Phantom Retreat (MIDNIGHT L5)
   * SRD: Two-phase spell:
   * 1. Spend Hope to mark current location
   * 2. Spend another Hope before next rest to teleport back
   */
  'phantom-retreat': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'midnight',
    level: 5,
    primaryAction: {
      id: 'markLocation',
      label: '📍 Mark Location (1H)',
      cost: { type: 'hope', amount: 1 },
      sessionStateKey: 'phantomRetreatLocation',
      description: 'Mark your current position for later retreat.',
    },
    secondaryAction: {
      id: 'teleportBack',
      label: '👻 Retreat (1H)',
      cost: { type: 'hope', amount: 1 },
      validation: { requiresPrimary: 'phantomRetreatLocation' },
      consumesPrimary: true,
      description: 'Teleport back to marked location (clears mark).',
    },
    description: 'Mark location (1H), then teleport back before rest (1H). Ends after reappearing.',
  },

  /**
   * Nature's Tongue (SAGE L1)
   * SRD: Two effects:
   * 1. Passive: Speak to plants/animals (Instinct Roll 12)
   * 2. Spend Hope for +2 to Spellcast in natural environment
   */
  'natures-tongue': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Ability',
    domain: 'sage',
    level: 1,
    primaryAction: {
      id: 'speakNature',
      label: '🌿 Speak to Nature',
      triggersPendingSpellAttack: {
        difficulty: 12,
        damage: null,
        rollType: 'Instinct',
        spellName: "Nature's Tongue",
        successMessage: 'Plants and animals share their knowledge!',
        fearMessage: 'Knowledge is limited or comes at a cost.',
      },
      description: 'Instinct Roll (12) to speak with plants and animals.',
    },
    secondaryAction: {
      id: 'spellcastBonus',
      label: '✨ +2 Spellcast (1H)',
      cost: { type: 'hope', amount: 1 },
      sessionStateKey: 'naturesTongueBonus',
      description: 'In natural environment: +2 to Spellcast Roll.',
    },
    description: 'Speak with nature (Instinct 12). In natural areas: spend Hope for +2 Spellcast.',
  },

  /**
   * Conjured Steeds (SAGE L6)
   * SRD: Spend any number of Hope to conjure that many magical steeds.
   * Last until long rest or steeds take damage.
   */
  'conjured-steeds': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'sage',
    level: 6,
    // Variable Hope cost - user selects amount
    handler: 'activateConjuredSteeds',
    sessionStateKey: 'conjuredSteedsCount',
    buttonLabel: {
      default: '🐎 Conjure Steeds (XH)',
      active: (count) => `🐎 ${count} Steed${count > 1 ? 's' : ''} Active`,
      disabled: { noHope: 'Need at least 1 Hope' },
    },
    description: 'Spend X Hope to conjure X steeds until long rest or they take damage. +2 damage, -2 attack while mounted.',
  },

  /**
   * Tactician (BONE L3)
   * SRD: Two passive effects:
   * 1. When you Help an Ally, they can spend Hope to add your Experience
   * 2. On Tag Team Rolls, roll d20 as Hope Die
   */
  'tactician': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 3,
    // Display-only passive - no button action needed
    passiveDisplay: true,
    buttonLabel: {
      default: '📋 Passive',
    },
    passiveIndicator: {
      color: 'blue',
      message: 'Help an Ally: they can spend Hope to add your Experience. Tag Team: roll d20 as Hope Die.',
    },
    description: 'Help an Ally: they spend Hope for +Experience. Tag Team: d20 as Hope Die.',
  },

  /**
   * Wrangle (BONE L8)
   * SRD: Make Agility Roll vs all targets in Close range.
   * Spend Hope to move targets you succeed against + willing allies.
   */
  'wrangle': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 8,
    cost: { type: 'hope', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: null, // Opposed roll vs targets
      damage: null,
      rollType: 'Agility',
      spellName: 'Wrangle',
      successMessage: 'Move targets and willing allies within Close range!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🤠 Wrangle (1H)',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'Agility Roll vs Close targets. Spend Hope to reposition targets you beat + willing allies.',
  },

  // ---------------------------------------------------------------------------
  // Group D: Spellcast Roll Spells
  // Issue #4 - January 2026 (21 cards)
  // ---------------------------------------------------------------------------

  // ===== GRACE Domain (5 cards) =====

  /**
   * Enrapture (GRACE L1)
   * SRD: Spellcast Roll vs Close target. On success, they become Enraptured.
   * Once per rest: mark Stress to force Enraptured target to mark Stress.
   */
  'enrapture': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 1,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      spellName: 'Enrapture',
      successMessage: '💫 Enrapture! Target is Enraptured - attention fixed on you, narrowed field of view.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '💫 Enrapture',
    },
    description: 'Spellcast vs Close target → Enraptured. Once/rest: mark Stress to force them to mark Stress.',
  },

  /**
   * Tell No Lies (GRACE L2)
   * SRD: Spellcast Roll vs Very Close target. On success, they can\'t lie while in Close range.
   * If they refuse to answer, they mark Stress and effect ends.
   */
  'tell-no-lies': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 2,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      spellName: 'Tell No Lies',
      successMessage: '🤥 Tell No Lies! Target cannot lie while in Close range. Refusal = mark Stress, spell ends.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🤥 Tell No Lies',
    },
    description: 'Spellcast vs Very Close target → they cannot lie while in Close range. Refusal ends spell.',
  },

  /**
   * Hypnotic Shimmer (GRACE L3)
   * SRD: Once per rest, Spellcast Roll vs all adversaries in front within Close.
   * On success: create illusion, Stun targets, force them to mark Stress.
   */
  'hypnotic-shimmer': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 3,
    tokenId: 'hypnotic-shimmer-charge',
    validation: { checkToken: true },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      isAoE: true,
      spellName: 'Hypnotic Shimmer',
      successMessage: '🌈 Hypnotic Shimmer! Targets are Stunned and mark Stress!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌈 Hypnotic Shimmer',
      disabled: { noToken: 'Used (rest)' },
    },
    description: 'Once/rest: Spellcast vs Close adversaries in front → Stunned + mark Stress.',
  },

  /**
   * Words of Discord (GRACE L5)
   * SRD: Spellcast (13) vs Melee adversary. On success: they mark Stress and attack another adversary.
   * -5 penalty on same target next time.
   */
  'words-of-discord': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 5,
    triggersPendingSpellAttack: {
      difficulty: 13,
      damage: null,
      damageType: null,
      spellName: 'Words of Discord',
      successMessage: '🗣️ Words of Discord! Target marks Stress and attacks another adversary!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🗣️ Words of Discord (DC 13)',
    },
    description: 'Spellcast (13) vs Melee target → they mark Stress and attack another adversary. -5 penalty on reuse.',
  },

  /**
   * Mass Enrapture (GRACE L8)
   * SRD: Spellcast Roll vs all targets within Far range. On success: Enraptured.
   * Mark Stress to force all Enraptured to mark Stress (ends spell).
   */
  'mass-enrapture': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 8,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      isAoE: true,
      spellName: 'Mass Enrapture',
      successMessage: '💫 Mass Enrapture! All targets within Far range are Enraptured!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '💫 Mass Enrapture',
    },
    description: 'Spellcast vs ALL targets within Far → Enraptured. Mark Stress to force ALL to mark Stress (ends spell).',
  },

  // ===== MIDNIGHT Domain (4 cards) =====

  /**
   * Glyph of Nightfall (MIDNIGHT L4)
   * SRD: Spellcast Roll vs Very Close target. On success, spend Hope to conjure glyph
   * that reduces target's Difficulty by Knowledge (min 1).
   */
  'glyph-of-nightfall': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 4,
    cost: { type: 'hope', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      spellName: 'Glyph of Nightfall',
      successMessage: '🔮 Glyph of Nightfall! Target Difficulty reduced by your Knowledge (min 1).',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🔮 Glyph of Nightfall (1H)',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'Spellcast vs Very Close target, spend Hope → reduce their Difficulty by Knowledge (min 1).',
  },

  /**
   * Hush (MIDNIGHT L5)
   * SRD: Spellcast Roll vs Close target. On success, spend Hope for Silenced condition
   * in Very Close area around target. No noise, no spells.
   */
  'hush': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 5,
    cost: { type: 'hope', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      spellName: 'Hush',
      successMessage: '🤫 Hush! Target and Very Close area are Silenced - no noise, no spells.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🤫 Hush (1H)',
      disabled: { noHope: 'Need 1 Hope' },
    },
    description: 'Spellcast vs Close target, spend Hope → Silenced until GM clears or you take Major damage.',
  },

  /**
   * Dark Whispers (MIDNIGHT L6)
   * SRD: Telepathy with physical-contact target. Mark Stress for Spellcast Roll
   * to ask one of four questions about them.
   */
  'dark-whispers': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 6,
    cost: { type: 'stress', amount: 1 },
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      spellName: 'Dark Whispers',
      successMessage: '🌑 Dark Whispers! Ask: Where? What doing? Afraid of? Cherishes most?',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌑 Dark Whispers (1S)',
    },
    description: 'Telepathy with touched target. Mark Stress + Spellcast → ask 1 of 4 questions.',
  },

  /**
   * Eclipse (MIDNIGHT L10)
   * SRD: Spellcast (16). Once per long rest: plunge Far area into darkness
   * only allies can see through. Attackers have disadvantage vs you/allies.
   */
  'eclipse': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 10,
    tokenId: 'eclipse-charge',
    tokenRefreshOn: 'long-rest',
    validation: { checkToken: true },
    triggersPendingSpellAttack: {
      difficulty: 16,
      damage: null,
      damageType: null,
      spellName: 'Eclipse',
      successMessage: '🌑 Eclipse! Far range plunged into darkness only allies can see through!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌑 Eclipse (DC 16)',
      disabled: { noToken: 'Used (long rest)' },
    },
    description: 'Once/long rest: Spellcast (16) → Far darkness. Attacks vs you/allies have disadvantage.',
  },

  // ===== SAGE Domain (4 cards) =====

  /**
   * Vicious Entangle (SAGE L1)
   * SRD: Spellcast Roll vs Far target. On success: d8+1 physical + Restrained.
   * Spend Hope to Restrain another Very Close adversary.
   */
  'vicious-entangle': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'sage',
    level: 1,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: '1d8+1',
      damageType: 'physical',
      spellName: 'Vicious Entangle',
      successMessage: '🌿 Vicious Entangle! Target takes d8+1 physical and is Restrained!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌿 Vicious Entangle',
    },
    description: 'Spellcast vs Far target → d8+1 physical + Restrained. Spend Hope to Restrain another.',
  },

  /**
   * Natural Familiar (SAGE L2)
   * SRD: Spend Hope to summon familiar until rest. +1H for flying.
   * Mark Stress to see through eyes. +d6 damage in Melee of familiar.
   */
  'natural-familiar': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'sage',
    level: 2,
    primaryAction: {
      id: 'summon',
      label: '🐿️ Summon (1H)',
      icon: '🐿️',
      cost: { type: 'hope', amount: 1 },
      sessionStateKey: 'naturalFamiliarActive',
      description: 'Summon nature spirit/critter until rest. +d6 damage in its Melee.',
    },
    secondaryAction: {
      id: 'flying',
      label: '🦉 Flying (2H)',
      icon: '🦉',
      cost: { type: 'hope', amount: 2 },
      sessionStateKey: 'naturalFamiliarActive',
      description: 'Summon a flying familiar. +d6 damage in its Melee.',
    },
    buttonLabel: {
      active: '🐿️ Familiar Active',
    },
    description: 'Summon familiar (1H) or flying (2H). Mark Stress to see through eyes. +d6 damage in its Melee.',
  },

  /**
   * Rejuvenation Barrier (SAGE L8)
   * SRD: Spellcast (15). Once per rest: create barrier at Very Close.
   * Allies heal 1d4 HP + resist physical from outside.
   */
  'rejuvenation-barrier': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'sage',
    level: 8,
    tokenId: 'rejuvenation-barrier-charge',
    validation: { checkToken: true },
    triggersPendingSpellAttack: {
      difficulty: 15,
      damage: null,
      damageType: null,
      healing: '1d4',
      spellName: 'Rejuvenation Barrier',
      successMessage: '🌿 Rejuvenation Barrier! Allies heal 1d4 HP. Resistance to physical from outside.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌿 Barrier (DC 15)',
      disabled: { noToken: 'Used (rest)' },
    },
    description: 'Once/rest: Spellcast (15) → barrier at Very Close. Allies heal 1d4 HP + resist physical.',
  },

  /**
   * Plant Dominion (SAGE L9)
   * SRD: Spellcast (18). Once per long rest: reshape plant life within Far range.
   */
  'plant-dominion': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'sage',
    level: 9,
    tokenId: 'plant-dominion-charge',
    tokenRefreshOn: 'long-rest',
    validation: { checkToken: true },
    triggersPendingSpellAttack: {
      difficulty: 18,
      damage: null,
      damageType: null,
      spellName: 'Plant Dominion',
      successMessage: '🌳 Plant Dominion! Reshape plant life within Far range!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌳 Plant Dominion (DC 18)',
      disabled: { noToken: 'Used (long rest)' },
    },
    description: 'Once/long rest: Spellcast (18) → reshape plant life within Far (grow trees, clear paths, root walls).',
  },

  // ===== SPLENDOR Domain (3 cards) =====

  /**
   * Final Words (SPLENDOR L2)
   * SRD: Spellcast (13) on corpse. Success with Hope: 3 questions.
   * Success with Fear: 1 question. Body turns to dust after.
   */
  'final-words': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 2,
    triggersPendingSpellAttack: {
      difficulty: 13,
      damage: null,
      damageType: null,
      spellName: 'Final Words',
      successMessage: '💀 Final Words! Corpse speaks - 3 questions (Hope) or 1 (Fear). Body turns to dust.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '💀 Final Words (DC 13)',
    },
    description: 'Spellcast (13) on corpse → 3 questions (Hope success) or 1 (Fear success). Body dusts after.',
  },

  /**
   * Overwhelming Aura (SPLENDOR L9)
   * SRD: Spellcast (15), spend 2 Hope. Presence = Spellcast until long rest.
   * Adversaries mark Stress when targeting you.
   */
  'overwhelming-aura': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 9,
    cost: { type: 'hope', amount: 2 },
    sessionStateKey: 'overwhelmingAuraActive',
    triggersPendingSpellAttack: {
      difficulty: 15,
      damage: null,
      damageType: null,
      spellName: 'Overwhelming Aura',
      successMessage: '✨ Overwhelming Aura! Presence = Spellcast. Adversaries mark Stress when targeting you.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '✨ Aura (DC 15, 2H)',
      active: '✨ Aura Active',
      disabled: { noHope: 'Need 2 Hope' },
    },
    description: 'Spellcast (15), spend 2 Hope → Presence = Spellcast until long rest. Attackers mark Stress.',
  },

  /**
   * Salvation Beam (SPLENDOR L9)
   * SRD: Spellcast (16). Mark any Stress to heal line of Far allies
   * (1 HP per Stress, divide as you wish).
   */
  'salvation-beam': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 9,
    triggersPendingSpellAttack: {
      difficulty: 16,
      damage: null,
      damageType: null,
      healing: 'variable',
      spellName: 'Salvation Beam',
      successMessage: '✨ Salvation Beam! Mark Stress to heal allies in line (1 HP per Stress).',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '✨ Salvation Beam (DC 16)',
    },
    description: 'Spellcast (16) → mark any Stress to heal line of Far allies (1 HP per Stress marked).',
  },

  // ===== ARCANA Domain (3 cards) =====

  /**
   * Rift Walker (ARCANA L6)
   * SRD: Spellcast (15). Place arcane marking. Next cast opens rift back to that spot.
   */
  'rift-walker': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 6,
    sessionStateKey: 'riftWalkerMarking',
    triggersPendingSpellAttack: {
      difficulty: 15,
      damage: null,
      damageType: null,
      spellName: 'Rift Walker',
      sessionStateOnSuccess: 'riftWalkerMarking',
      successMessage: '✨ Rift Walker! Arcane marking placed. Cast again to open rift back here.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '✨ Rift Walker (DC 15)',
      active: '🌀 Open Rift',
    },
    description: 'Spellcast (15): Place marking. Next cast opens rift back to that spot.',
  },

  /**
   * Telekinesis (ARCANA L6)
   * SRD: Spellcast Roll vs Far target. Move them within Far range.
   * Second Spellcast to throw: d12+4 physical with Proficiency.
   */
  'telekinesis': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 6,
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: '1d12+4',
      damageType: 'physical',
      usesProficiency: true,
      spellName: 'Telekinesis',
      successMessage: '🧲 Telekinesis! Target lifted. Make second Spellcast to throw (d12+4 physical).',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🧲 Telekinesis',
    },
    description: 'Spellcast vs Far target → lift and move. Throw for d12+4 physical (second roll).',
  },

  /**
   * Sensory Projection (ARCANA L9)
   * SRD: Once per rest, Spellcast (15). Vision of any visited location.
   * Ends on damage or casting another spell.
   */
  'sensory-projection': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 9,
    tokenId: 'sensory-projection-charge',
    validation: { checkToken: true },
    triggersPendingSpellAttack: {
      difficulty: 15,
      damage: null,
      damageType: null,
      spellName: 'Sensory Projection',
      successMessage: '👁️ Sensory Projection! You can see/hear any visited location. Undetectable.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '👁️ Project Senses (DC 15)',
      disabled: { noToken: 'Used (rest)' },
    },
    description: 'Once/rest: Spellcast (15) to see/hear any visited location. Ends on damage or other spell.',
  },

  // ===== CODEX Domain (2 cards) =====

  /**
   * Manifest Wall (CODEX L5)
   * SRD: Spellcast (15). Once per rest, spend Hope to create magical wall
   * between two Far points. Up to 50ft high.
   */
  'manifest-wall': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'codex',
    level: 5,
    cost: { type: 'hope', amount: 1 },
    tokenId: 'manifest-wall-charge',
    validation: { checkToken: true, requireHope: 1 },
    triggersPendingSpellAttack: {
      difficulty: 15,
      damage: null,
      damageType: null,
      spellName: 'Manifest Wall',
      successMessage: '🧱 Manifest Wall! Magical wall created between two Far points (up to 50ft high).',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🧱 Manifest Wall (DC 15, 1H)',
      disabled: {
        noToken: 'Used (rest)',
        noHope: 'Need 1 Hope',
      },
    },
    description: 'Once/rest: Spellcast (15), spend Hope → magical wall between Far points. Lasts until rest.',
  },

  /**
   * Banish (CODEX L6)
   * SRD: Spellcast Roll vs Close target. Roll Spellcast×d20s.
   * Target reacts vs highest or is banished (once/rest on failure).
   */
  'banish': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'codex',
    level: 6,
    tokenId: 'banish-charge',
    triggersPendingSpellAttack: {
      difficulty: null, // Contested
      damage: null,
      damageType: null,
      spellName: 'Banish',
      successMessage: '🌀 Banish! Roll Spellcast×d20s. Target reacts vs highest or is Banished!',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🌀 Banish',
      disabled: { noToken: 'Banished (rest)' },
    },
    description: 'Spellcast vs Close → roll Spellcast×d20s. Target reacts or is banished (once/rest).',
  },

  // ===========================================================================
  // TOKEN MIGRATION CARDS (Group F - Token/Accumulator Systems)
  // These cards have token logic in SimpleTokenService.js and need UI config
  // ===========================================================================

  // ===== ARCANA Domain =====

  /**
   * Flight (ARCANA L3)
   * SRD: Spellcast Roll (15). Place tokens = Agility. Spend 1 token per action while flying.
   * After last token spent, descend to ground.
   */
  'flight': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 3,
    triggersPendingSpellAttack: {
      difficulty: 15,
      damage: null,
      damageType: null,
      spellName: 'Flight',
      handler: 'activateFlightTokens',
      successMessage: '🪽 Flight activated! Tokens placed equal to Agility. Spend 1 per action.',
    },
    switchToTab: 'combat',
    buttonLabel: {
      default: '🪽 Flight (DC 15)',
      active: '🪽 Flying',
    },
    sessionStateKey: 'flightActive',
    description: 'Spellcast (15): Fly for the scene. Spend 1 token per action while flying.',
    tokenConfig: {
      id: 'flight-tokens',
      name: 'Flight Tokens',
      getMax: (char) => Math.max(1, char?.traits?.Agility || 1),
      refreshOn: 'manual',
      requiresActivation: true,
    },
  },

  // ===== GRACE Domain =====

  /**
   * Invisibility (GRACE L3)
   * SRD: Spellcast Roll (10). Mark Stress, choose self or ally within Melee to become Invisible.
   * Place tokens = Spellcast trait. Spend 1 token per action while invisible.
   */
  'invisibility': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 3,
    handler: 'activateInvisibility',
    handlerArgs: ['Finesse'], // Grace spellcast trait
    buttonLabel: {
      default: '👻 Invisibility (DC 10)',
      active: '👻 Invisible',
    },
    buttonIcon: '👻',
    sessionStateKey: 'invisibilityActive',
    description: 'Spellcast (10) + mark Stress: Become Invisible. Spend 1 token per action.',
    successIcon: '👻',
    tokenConfig: {
      id: 'invisibility-tokens',
      name: 'Invisibility Tokens',
      getMax: (char) => {
        // SRD: "tokens equal to your Spellcast trait" (Grace domain → Finesse)
        const traits = char?.traits || {};
        return Math.max(1, traits.Finesse || 2);
      },
      refreshOn: 'manual',
      requiresActivation: true,
    },
  },

  /**
   * Never Upstaged (GRACE L6)
   * SRD: When you mark HP from attack, mark Stress to place tokens = HP marked.
   * Next successful attack: +5 damage per token, then clear all.
   * REACTIVE: Auto-triggers via useHPDamageMode.js integration.
   */
  'never-upstaged': {
    pattern: PATTERN_TYPES.ACCUMULATOR,
    cardType: 'Ability',
    domain: 'grace',
    level: 6,
    tokenId: 'upstage-tokens',
    reactive: true, // Auto-triggers on HP damage via useHPDamageMode.js
    spendAllOnAttack: true,
    damagePerToken: 5,
    description: 'When you mark HP: mark Stress to gain tokens. Next attack: +5 damage per token.',
    successIcon: '⚔️',
    tokenConfig: {
      id: 'upstage-tokens',
      name: 'Upstage Tokens',
      max: 99,
      refreshOn: 'on-use',
    },
  },

  /**
   * Notorious (GRACE L10)
   * SRD: Mark Stress before rolling to gain +10 to the result.
   * Free food/drinks, discounts on purchases.
   * SPECIAL: Does not count toward loadout's 5-card maximum. Cannot be vaulted.
   */
  'notorious': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'grace',
    level: 10,
    cost: { type: 'stress', amount: 1 },
    handler: 'activateNotorious',
    sessionStateKey: 'notoriousBonus',
    bonusValue: 10,
    buttonLabel: {
      default: '+10 Bonus (1S)',
      active: '+10 Ready',
      disabled: {
        noStress: 'No Stress',
      },
    },
    buttonIcon: '⭐',
    description: 'Mark Stress for +10 to next roll. Free food/drinks everywhere.',
    successIcon: '⭐',
    meta: {
      ignoreLoadoutLimit: true,
      cannotVault: true,
    },
  },

  // ===== MIDNIGHT Domain =====

  /**
   * Uncanny Disguise (MIDNIGHT L1)
   * SRD: Mark Stress to don humanoid disguise. Place tokens = Spellcast trait.
   * Spend 1 token per action while disguised. Advantage on Presence rolls to avoid scrutiny.
   */
  'uncanny-disguise': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 1,
    cost: { type: 'stress', amount: 1 },
    handler: 'activateUncannyDisguise',
    tokenId: 'disguise-tokens',
    buttonLabel: {
      default: '🎭 Disguise (1S)',
      active: '🎭 Disguised',
    },
    buttonIcon: '🎭',
    sessionStateKey: 'disguiseActive',
    description: 'Mark Stress: Perfect disguise. Spend 1 token per action. Advantage on Presence.',
    successIcon: '🎭',
    tokenConfig: {
      id: 'disguise-tokens',
      name: 'Disguise Tokens',
      getMax: (char) => {
        const traits = char?.traits || {};
        return Math.max(1, traits.Presence || traits.Instinct || traits.Knowledge || 2);
      },
      refreshOn: 'manual',
      requiresActivation: true,
      cost: { type: 'stress', amount: 1 },
    },
  },

  /**
   * Spellcharge (MIDNIGHT L8)
   * SRD: When you take magic damage, place tokens = HP marked (max = Spellcast trait).
   * On successful attack, spend tokens for +d6 damage each.
   * REACTIVE: Auto-triggers via useHPDamageMode.js integration.
   */
  'spellcharge': {
    pattern: PATTERN_TYPES.ACCUMULATOR,
    cardType: 'Spell',
    domain: 'midnight',
    level: 8,
    tokenId: 'charge-tokens',
    reactive: true, // Auto-triggers on magic damage via useHPDamageMode.js
    spendForDice: true,
    dicePerToken: { count: 1, sides: 6 },
    description: 'When you take magic damage: gain tokens (max Spellcast). Spend for +d6 damage each.',
    successIcon: '⚡',
    tokenConfig: {
      id: 'charge-tokens',
      name: 'Charge Tokens',
      getMax: (char) => {
        const traits = char?.traits || {};
        return Math.max(2, Math.max(traits.Presence || 0, traits.Instinct || 0, traits.Knowledge || 0, traits.Finesse || 0));
      },
      refreshOn: 'manual',
      die: 'd6',
    },
  },

  // ===== SAGE Domain =====

  /**
   * Thorn Skin (SAGE L5)
   * SRD: Once per rest, spend Hope to sprout thorns. Place tokens = Spellcast trait.
   * When taking damage, spend tokens to roll d6s and reduce damage + reflect to attacker.
   */
  'thorn-skin': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'sage',
    level: 5,
    tokenId: 'thorn-tokens',
    primaryAction: {
      id: 'activate',
      label: 'Activate (1H)',
      icon: '🌿',
      cost: { type: 'hope', amount: 1 },
      handler: 'activateThornSkin',
      validation: { requireHope: 1 },
      description: 'Spend Hope to place Spellcast tokens.',
    },
    secondaryAction: {
      id: 'deflect',
      label: 'Deflect (1d6)',
      icon: '🛡️',
      variant: 'support',
      spendToken: true,
      rollDice: { count: 1, sides: 6, type: 'reduction' },
      description: 'Spend token: reduce damage by d6, reflect to attacker within Melee.',
    },
    description: 'Spend Hope for tokens. Spend token: d6 damage reduction + reflect to attacker.',
    tokenConfig: {
      id: 'thorn-tokens',
      name: 'Thorn Tokens',
      getMax: (char) => {
        const traits = char?.traits || {};
        return Math.max(1, traits.Presence || traits.Instinct || traits.Knowledge || 2);
      },
      refreshOn: 'rest',
      requiresActivation: true,
      cost: { type: 'hope', amount: 1 },
    },
  },

  /**
   * Wild Fortress (SAGE L5)
   * SRD: Spellcast Roll (13), spend 2 Hope to create protective dome.
   * Can't be targeted or attack while inside. Dome destroyed at 3 HP (15/30 thresholds).
   */
  'wild-fortress': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'sage',
    level: 5,
    tokenId: 'fortress-hp',
    primaryAction: {
      id: 'create',
      label: 'Create (2H, DC 13)',
      icon: '🏰',
      cost: { type: 'hope', amount: 2 },
      triggersPendingSpellAttack: {
        difficulty: 13,
        damage: null,
        damageType: null,
        spellName: 'Wild Fortress',
        handler: 'activateWildFortress',
        successMessage: '🏰 Wild Fortress created! Dome has 3 HP (15/30 damage thresholds).',
      },
      switchToTab: 'combat',
      validation: { requireHope: 2 },
      description: 'Spellcast (13) + 2 Hope: Create protective dome.',
    },
    secondaryAction: {
      id: 'damage',
      label: '+1 Dome HP',
      icon: '💔',
      variant: 'warning',
      incrementToken: true,
      maxToken: 3,
      destroyOnMax: true,
      description: 'Mark dome damage. Destroyed at 3 HP.',
    },
    description: 'Spellcast (13) + 2 Hope: Dome protects you + 1 ally. Destroyed at 3 HP.',
    tokenConfig: {
      id: 'fortress-hp',
      name: 'Fortress Hit Points',
      max: 3,
      refreshOn: 'manual',
      requiresActivation: true,
      cost: { type: 'hope', amount: 2 },
    },
  },

  /**
   * Fane of the Wilds (SAGE L9)
   * SRD: After long rest, place tokens = number of Sage cards in loadout + vault.
   * Spend tokens after Spellcast roll for +1 bonus each. Gain token on Sage spell crit.
   */
  'fane-of-the-wilds': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Ability',
    domain: 'sage',
    level: 9,
    tokenId: 'fane-tokens',
    primaryAction: {
      id: 'boost',
      label: '+1 Spellcast',
      icon: '🌿',
      variant: 'primary',
      spendToken: true,
      sessionStateKey: 'faneBoostActive',
      description: 'Spend token for +1 to Spellcast roll.',
    },
    secondaryAction: {
      id: 'critGain',
      label: 'Crit +1',
      icon: '✨',
      variant: 'support',
      gainToken: true, // Manual button for GM-confirmed Sage spell crit
      description: 'Gain token when you crit on a Sage domain spell.',
    },
    description: 'Long rest: tokens = Sage cards. Spend for +1 Spellcast. Gain on Sage crits.',
    tokenConfig: {
      id: 'fane-tokens',
      name: 'Fane Tokens',
      max: 3, // Default; should count Sage cards in loadout
      refreshOn: 'long-rest',
    },
  },

  // ===== SPLENDOR Domain =====

  /**
   * Restoration (SPLENDOR L6)
   * SRD: After long rest, place tokens = Spellcast trait.
   * Touch creature, spend tokens to clear 2 HP or 2 Stress per token.
   * Can also clear Vulnerable or heal ailments.
   */
  'restoration': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'splendor',
    level: 6,
    tokenId: 'restoration-tokens',
    primaryAction: {
      id: 'healHP',
      label: 'Heal 2 HP',
      icon: '❤️',
      variant: 'healing',
      spendToken: true,
      heal: { type: 'hp', amount: 2 },
      description: 'Spend token to heal 2 HP on target.',
    },
    secondaryAction: {
      id: 'healStress',
      label: 'Clear 2 Stress',
      icon: '💙',
      variant: 'support',
      spendToken: true,
      heal: { type: 'stress', amount: 2 },
      description: 'Spend token to clear 2 Stress on target.',
    },
    description: 'Long rest: tokens = Spellcast. Spend to heal 2 HP or clear 2 Stress per token.',
    tokenConfig: {
      id: 'restoration-tokens',
      name: 'Restoration Tokens',
      getMax: (char) => {
        const traits = char?.traits || {};
        return Math.max(1, traits.Presence || traits.Instinct || traits.Knowledge || 2);
      },
      refreshOn: 'long-rest',
    },
  },

  // ---------------------------------------------------------------------------
  // Transformation Pattern Cards
  // ---------------------------------------------------------------------------

  /**
   * Frenzy (BLADE L8)
   * SRD: Once per long rest, enter Frenzy until no more adversaries within sight.
   * While Frenzied: can't use Armor Slots, +10 damage rolls, +8 Severe threshold.
   */
  'frenzy': {
    pattern: PATTERN_TYPES.TRANSFORMATION,
    cardType: 'Ability',
    domain: 'blade',
    level: 8,
    tokenId: 'frenzy-uses',
    validation: { checkToken: true, preventIfActive: true },
    sessionStateKey: 'frenzyActive',
    transformationName: 'Frenzied',
    effects: {
      damageBonus: 10,
      severeThresholdBonus: 8,
      restrictions: ['Cannot use Armor Slots'],
    },
    deactivation: {
      type: 'manual',
      gmDetermined: true,
    },
    buttonLabel: {
      default: '🔥 Enter Frenzy',
      active: '🔥 Frenzied',
      disabled: { noToken: 'Used this rest' },
    },
    activeIndicator: { color: 'red', icon: '🔥' },
    description: 'Once per long rest: +10 damage, +8 Severe threshold, cannot use Armor Slots. Until no adversaries in sight.',
    tokenConfig: {
      id: 'frenzy-uses',
      name: 'Frenzy',
      max: 1,
      refreshOn: 'long-rest',
    },
  },

  /**
   * Specter of the Dark (MIDNIGHT L10)
   * SRD: Mark Stress to become Spectral until action roll targeting another creature.
   * While Spectral: immune to physical damage, float, pass through objects.
   */
  'specter-of-the-dark': {
    pattern: PATTERN_TYPES.TRANSFORMATION,
    cardType: 'Spell',
    domain: 'midnight',
    level: 10,
    cost: { type: 'stress', amount: 1 },
    validation: { preventIfActive: true },
    sessionStateKey: 'specterActive',
    transformationName: 'Spectral',
    effects: {
      immunities: ['Physical damage'],
      abilities: ['Float', 'Pass through solid objects'],
      notes: ['Other creatures can still see you'],
    },
    deactivation: {
      type: 'on-action-roll',
      triggerDescription: 'Ends when you make an action roll targeting another creature',
    },
    buttonLabel: {
      default: '👻 Become Spectral (1S)',
      active: '👻 Spectral Form',
      disabled: { noStress: 'No Stress' },
    },
    activeIndicator: { color: 'purple', icon: '👻' },
    description: 'Mark Stress to become Spectral. Immune to physical damage, float through objects. Ends on action roll targeting creature.',
  },

  /**
   * Force of Nature (SAGE L10)
   * SRD: Mark Stress to transform into hulking nature spirit.
   * Must spend Hope before each action roll. +10 damage on success.
   * Absorb defeated creatures within Close to clear Armor Slot. Immune to Restrained.
   */
  'force-of-nature': {
    pattern: PATTERN_TYPES.TRANSFORMATION,
    cardType: 'Spell',
    domain: 'sage',
    level: 10,
    cost: { type: 'stress', amount: 1 },
    validation: { preventIfActive: true },
    sessionStateKey: 'forceOfNatureActive',
    transformationName: 'Nature Spirit',
    effects: {
      damageBonus: 10,
      abilities: ['Absorb defeated creatures within Close to clear Armor Slot'],
      immunities: ['Cannot be Restrained'],
      maintenanceCost: '1 Hope per action roll (auto-deducted)',
    },
    deactivation: {
      type: 'manual',
      hopeMaintenance: true,
    },
    buttonLabel: {
      default: '🌿 Transform (1S)',
      active: '🌿 Nature Spirit',
      disabled: { noStress: 'No Stress' },
    },
    activeIndicator: { color: 'green', icon: '🌿' },
    description: 'Mark Stress to transform. +10 damage on success, absorb defeated for Armor, immune to Restrained. Costs 1 Hope per action roll.',
  },

  /**
   * Thought Delver (GRACE L5)
   * SRD: Spend Hope to read surface thoughts within Far range.
   * Make Spellcast Roll for deeper thoughts. On Fear, target might become aware.
   */
  'thought-delver': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'grace',
    level: 5,
    primaryAction: {
      id: 'surface',
      label: 'Surface (1H)',
      icon: '🔮',
      cost: { type: 'hope', amount: 1 },
      handler: 'spendHopeForThoughtDelver',
      validation: { requireHope: 1 },
      description: 'Spend Hope to read vague surface thoughts within Far range.',
    },
    secondaryAction: {
      id: 'deep',
      label: 'Deep Dive',
      icon: '🧠',
      triggersPendingSpellAttack: {
        spellName: 'Thought Delver (Deep)',
        damage: null,
        damageType: 'psychic',
        successMessage: 'You delve into their deeper, hidden thoughts.',
        fearMessage: 'The target might become aware that you are reading their thoughts.',
      },
      switchToTab: 'combat',
      description: 'Spellcast Roll against target for deeper thoughts. On Fear, target may sense you.',
    },
    description: 'Spend Hope for surface thoughts. Spellcast Roll for deeper thoughts (Fear may alert target).',
  },

  // ---------------------------------------------------------------------------
  // PermanentChoice Pattern Cards - Vault Cards with One-Time Bonuses
  // ---------------------------------------------------------------------------

  'vitality': {
    pattern: PATTERN_TYPES.PERMANENT_CHOICE,
    cardType: 'Passive',
    domain: 'blade',
    level: 5,
    maxChoices: 2,
    choiceMode: 'multiple',
    options: [
      {
        key: 'maxStress',
        label: '+1 Stress Slot',
        description: 'Permanently gain an additional Stress slot',
      },
      {
        key: 'maxHP',
        label: '+1 HP Slot',
        description: 'Permanently gain an additional Hit Point slot',
      },
      {
        key: 'maxThreshold',
        label: '+2 Damage Thresholds',
        description: 'Permanently gain +2 to all damage thresholds',
      },
    ],
    buttonLabel: {
      default: 'Choose Bonuses',
    },
    modalTitle: 'Vitality',
    modalDescription: 'Choose 2 of the following permanent bonuses. This card will then move to your vault.',
    successMessage: 'Vitality bonuses permanently applied!',
    icon: '💪',
    description: 'Choose 2: +1 Stress slot, +1 HP slot, or +2 thresholds. Then vault this card.',
    meta: { isVaultCard: true },
  },

  'master-of-the-craft': {
    pattern: PATTERN_TYPES.PERMANENT_CHOICE,
    cardType: 'Passive',
    domain: 'grace',
    level: 9,
    maxChoices: 1,
    choiceMode: 'exclusive',
    options: [
      {
        key: 'twoExperiences',
        label: '+2 to Two Experiences',
        description: 'Choose 2 Experiences to gain +2 each',
      },
      {
        key: 'oneExperience',
        label: '+3 to One Experience',
        description: 'Choose 1 Experience to gain +3',
      },
    ],
    buttonLabel: {
      default: 'Choose Bonus Mode',
    },
    modalTitle: 'Master of the Craft',
    modalDescription: 'Choose how to apply your Experience bonuses. This card will then move to your vault.',
    successMessage: 'Master of the Craft: Select your Experiences!',
    icon: '🎭',
    description: '+2 to two Experiences OR +3 to one Experience. Then vault this card.',
    meta: { isVaultCard: true, requiresExperienceSelection: true },
  },

  // ---------------------------------------------------------------------------
  // PASSIVE / DISPLAY-ONLY CARDS (mechanics in specialized managers)
  // ---------------------------------------------------------------------------

  // Soothing Speech (GRACE L4) - Downtime healing bonus
  // Mechanics handled in DowntimeManager.js
  'soothing-speech': {
    pattern: null, // No UI button - auto-applies during Tend to Wounds
    cardType: 'Passive',
    domain: 'grace',
    level: 4,
    description: 'During short rest Tend to Wounds: clear extra HP on target, also clear 2 HP on self.',
    meta: { triggersOnDowntime: true, downtimeMove: 'tendToWounds' },
  },

  // Inevitable (VALOR L6) - Advantage after failed roll
  // Mechanics handled in DiceRoller.js
  'inevitable': {
    pattern: null, // No UI button - auto-tracks in DiceRoller
    cardType: 'Passive',
    domain: 'valor',
    level: 6,
    description: 'When you fail an action roll, your next action roll has advantage.',
    meta: { triggersOnFailedRoll: true },
  },

  // Twilight Toll (MIDNIGHT L9) - Token accumulator for damage
  // Mechanics handled in DamageModifierService.js + DiceRoller.js
  'twilight-toll': {
    pattern: null, // Complex targeting + token mechanics handled elsewhere
    cardType: 'Ability',
    domain: 'midnight',
    level: 9,
    description: 'Target in Far range. Successful non-damage rolls place tokens. Spend tokens for +d12 per token on damage.',
    meta: { usesTargeting: true, accumulatesTokens: true },
    tokenConfig: {
      id: 'toll-tokens',
      name: 'Toll Tokens',
      max: 99,
      refreshOn: 'target-change',
      die: 'd12',
    },
  },

  // Body Basher (VALOR L2) - Passive melee damage bonus
  // Mechanics handled in DiceRoller.js (damage calculation)
  'body-basher': {
    pattern: null, // No UI button - auto-adds Strength to melee damage
    cardType: 'Passive',
    domain: 'valor',
    level: 2,
    description: 'On successful melee weapon attack, add Strength to damage roll.',
    meta: { triggersOnMeleeDamage: true },
  },

  // On the Brink (BONE L9) - Ignore Minor damage at low HP
  // Mechanics handled in useVitalsManager.js
  'on-the-brink': {
    pattern: null, // No UI button - auto-ignores Minor damage
    cardType: 'Passive',
    domain: 'bone',
    level: 9,
    description: 'When you have 2 or fewer HP unmarked, ignore Minor (1 HP) damage.',
    meta: { triggersOnDamage: true, hpThreshold: 2 },
  },

  // Pick and Pull (MIDNIGHT L1) - Passive advantage on theft/locks/traps
  // Display only - advantage is narrative reminder (no mechanical auto-apply)
  'pick-and-pull': {
    pattern: null, // No UI button - passive advantage reminder
    cardType: 'Passive',
    domain: 'midnight',
    level: 1,
    description: 'Advantage on rolls to pick nonmagical locks, disarm nonmagical traps, or steal items.',
    meta: { grantsAdvantage: true, advantageContext: 'locks, traps, stealing' },
  },

  // Voice of Reason (SPLENDOR L3) - Passive advantage + full-stress bonus
  // Display only - advantage is narrative reminder
  'voice-of-reason': {
    pattern: null, // No UI button - passive advantage reminder
    cardType: 'Passive',
    domain: 'splendor',
    level: 3,
    description: 'Advantage on de-escalation or leading others. At full Stress: +1 Proficiency to damage rolls.',
    meta: { grantsAdvantage: true, advantageContext: 'de-escalation, leadership', stressBonusAtMax: true },
  },

  /**
   * Swift Step (BONE L10) - DEFERRED
   *
   * This card requires encounter/attack tracking which is not yet implemented:
   * - Need to detect incoming attacks against the player
   * - Need attack success/failure detection for enemy rolls
   *
   * This is out of scope for Issue #4 - tracked separately.
   * DO NOT flag this in audits until encounter system exists.
   *
   * SRD: "When an attack made against you fails, clear a Stress.
   *       If you can't clear a Stress, gain a Hope."
   */
  'swift-step': {
    pattern: null, // Requires encounter tracking - see comment above
    cardType: 'Reaction',
    domain: 'bone',
    level: 10,
    description: 'When an attack against you fails, clear 1 Stress. If no Stress to clear, gain 1 Hope.',
    meta: { triggersOnDefenseSuccess: true, clearsStress: 1, grantsHopeIfNoStress: true },
  },

  // Unyielding Armor (VALOR L10) - Reactive dice roll on armor mark
  // Implementation: SINGLE_ACTION pattern with custom handler that rolls Prof d6s
  // Player clicks when about to mark armor slot; success (any 6) reduces severity
  'unyielding-armor': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Reaction',
    domain: 'valor',
    level: 10,
    buttonLabel: {
      default: '🛡️ Roll to Resist',
    },
    description: 'When marking Armor Slot, roll d6s = Proficiency. On any 6: reduce severity by one threshold without marking slot.',
    handler: 'rollUnyieldingArmor',
    successIcon: '🛡️',
    meta: { triggersOnArmorMark: true, rollsD6: true, usesProf: true },
  },

  // ---------------------------------------------------------------------------
  // MEDIUM PRIORITY CARDS (2 SP each)
  // ---------------------------------------------------------------------------

  // Through Your Eyes (GRACE L4) - Duration spell with toggle
  // SRD: "Choose a target within Very Far range. You can see through their eyes
  // and hear through their ears. Until you cast another spell or rest."
  'through-your-eyes': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 4,
    sessionStateKey: 'throughYourEyesActive',
    buttonLabel: {
      default: 'Link Senses',
      active: 'Linked (click to end)',
    },
    buttonIcon: '👁️',
    activeDuration: null, // Manual toggle - ends on spell cast or rest
    description: 'Target in Very Far range. See/hear through their senses until you cast another spell or rest.',
    meta: { endsOnSpellCast: true, endsOnRest: true },
  },

  // Know Thy Enemy (BONE L5) - Instinct Roll + Hope spend + info choice
  // SRD: "Make an Instinct Roll against them. On success, spend Hope and ask GM for info."
  'know-thy-enemy': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Ability',
    domain: 'bone',
    level: 5,
    primaryAction: {
      id: 'hpStress',
      label: 'HP/Stress',
      icon: '💔',
      cost: { type: 'hope', amount: 1 },
      validation: { requireHope: 1 },
      description: 'Ask GM for target\'s unmarked HP and Stress.',
    },
    secondaryAction: {
      id: 'diffThresh',
      label: 'Diff/Thresh',
      icon: '🎯',
      cost: { type: 'hope', amount: 1 },
      validation: { requireHope: 1 },
      description: 'Ask GM for target\'s Difficulty and damage thresholds.',
    },
    description: 'Instinct Roll vs target. On success, spend Hope to learn: HP/Stress OR Difficulty/Thresholds.',
    meta: { requiresInstinctRoll: true, infoOptions: ['hpStress', 'diffThresh', 'tactics', 'features'] },
  },

  // Death Grip (SAGE L4) - Spellcast Roll with choice
  // SRD: "Make a Spellcast Roll against Close target and choose: pull into Melee OR force 2 Stress."
  'death-grip': {
    pattern: PATTERN_TYPES.DUAL_BUTTON,
    cardType: 'Spell',
    domain: 'sage',
    level: 4,
    primaryAction: {
      id: 'pull',
      label: 'Pull',
      icon: '🧲',
      triggersPendingSpellAttack: {
        difficulty: null, // Contested roll
        damage: null, // No damage
        effect: 'Pull target into Melee range OR pull yourself to them',
      },
      description: 'Pull target to Melee or move yourself to them.',
    },
    secondaryAction: {
      id: 'constrict',
      label: 'Constrict',
      icon: '💀',
      variant: 'attack',
      triggersPendingSpellAttack: {
        difficulty: null, // Contested roll
        damage: null, // No damage
        effect: 'Target marks 2 Stress',
        stressToTarget: 2,
      },
      description: 'Force target to mark 2 Stress.',
    },
    description: 'Spellcast Roll vs Close target. Choose: Pull to Melee OR force 2 Stress.',
    meta: { requiresSpellcastRoll: true },
  },

  // Forager (SAGE L6) - Downtime d6 roll
  // SRD: "As downtime move, roll d6 to see what you forage."
  'forager': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'sage',
    level: 6,
    handler: 'rollForagerD6',
    buttonLabel: {
      default: 'Forage (d6)',
    },
    buttonIcon: '🌿',
    forageTable: [
      { roll: 1, item: 'Unique Food', effect: 'Clear 2 Stress', icon: '🍖' },
      { roll: 2, item: 'Beautiful Relic', effect: 'Gain 2 Hope', icon: '💎' },
      { roll: 3, item: 'Arcane Rune', effect: '+2 to Spellcast Roll', icon: '🔮' },
      { roll: 4, item: 'Healing Vial', effect: 'Clear 2 HP', icon: '🧪' },
      { roll: 5, item: 'Luck Charm', effect: 'Reroll any die', icon: '🍀' },
      { roll: 6, item: 'Player Choice', effect: 'Choose any above', icon: '⭐' },
    ],
    description: 'Downtime move: Roll d6 to forage consumable. Party can carry up to 5.',
    meta: { isDowntimeMove: true, maxPartyConsumables: 5 },
  },

  // Goad Them On (VALOR L4) - Presence Roll taunt
  // SRD: "Make a Presence Roll against Close target. On success: target marks Stress,
  // must attack you next spotlight, attacks with disadvantage."
  'goad-them-on': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'valor',
    level: 4,
    handler: 'activateGoadThemOn',
    sessionStateKey: 'goadThemOnActive',
    buttonLabel: {
      default: 'Taunt',
      active: 'Taunting',
    },
    buttonIcon: '😤',
    description: 'Presence Roll vs Close target. Success: target marks Stress, must attack you (with disadvantage) on next spotlight.',
    meta: { requiresPresenceRoll: true, forcesTargetAttack: true, grantsDisadvantageToTarget: true },
  },

  // Unbreakable (VALOR L10) - Death move interception
  // This card has no active UI - it triggers automatically at Death's Door
  // The logic is handled in DeathManager.js
  'unbreakable': {
    pattern: null, // No pattern - triggers at death, not through UI
    cardType: 'Passive',
    domain: 'valor',
    level: 10,
    description: 'When you mark your last HP, roll d6 and clear that many HP instead of making a death move. Then vault this card permanently.',
    meta: { isVaultCard: true, triggersAtDeath: true },
  },

  // ---------------------------------------------------------------------------
  // PASSIVE STAT MODIFIER CARDS
  // These cards have automated stat effects in characterCalculations.js
  // No UI buttons needed - just display config for audit tracking
  // ---------------------------------------------------------------------------

  // Bare Bones (VALOR L1) - Unarmored defense + custom thresholds
  // Mechanics in calculateUnarmoredAC() and calculateBareBonesThresholds()
  'bare-bones': {
    pattern: null, // No UI button - auto-calculates armor/thresholds
    cardType: 'Passive',
    domain: 'valor',
    level: 1,
    description: 'While unarmored: Armor Score = 3 + Strength. Custom damage thresholds (no Minor, tier-based Major/Severe).',
    passiveBonus: {
      type: 'unarmoredDefense',
      formula: '3 + Strength',
      affectsStat: 'armorScore',
      condition: 'unarmored',
    },
    meta: { calculatedIn: 'characterCalculations.js', functions: ['calculateUnarmoredAC', 'calculateBareBonesThresholds'] },
  },

  // Fortified Armor (BLADE L4) - Threshold bonus while armored
  // Mechanics in calculateDamageThresholds()
  'fortified-armor': {
    pattern: null, // No UI button - auto-adds to thresholds
    cardType: 'Passive',
    domain: 'blade',
    level: 4,
    description: '+2 to all damage thresholds (Minor, Major, Severe) while wearing armor.',
    passiveBonus: {
      type: 'threshold',
      stat: 'allThresholds',
      value: 2,
      condition: 'armored',
    },
    meta: { calculatedIn: 'characterCalculations.js', functions: ['calculateDamageThresholds'] },
  },

  // Untouchable (BONE L1) - Evasion bonus from Agility
  // Mechanics in calculateEvasion()
  'untouchable': {
    pattern: null, // No UI button - auto-adds to evasion
    cardType: 'Passive',
    domain: 'bone',
    level: 1,
    description: '+half Agility (rounded down) to Evasion.',
    passiveBonus: {
      type: 'evasion',
      formula: 'floor(Agility / 2)',
      affectsStat: 'evasion',
    },
    meta: { calculatedIn: 'characterCalculations.js', functions: ['calculateEvasion'] },
  },

  // Shadowhunter (MIDNIGHT L8) - Evasion + advantage in darkness
  // Mechanics in calculateEvasion()
  'shadowhunter': {
    pattern: null, // No UI button - auto-adds to evasion
    cardType: 'Passive',
    domain: 'midnight',
    level: 8,
    description: '+1 Evasion. Advantage on attacks while in low light or darkness.',
    passiveBonus: {
      type: 'evasion',
      value: 1,
      affectsStat: 'evasion',
      additionalEffect: 'advantage in darkness',
    },
    meta: { calculatedIn: 'characterCalculations.js', functions: ['calculateEvasion'], grantsAdvantage: true, advantageContext: 'low light, darkness' },
  },

  // Armorer (VALOR L5) - Armor score bonus while armored
  // Mechanics in calculateArmorScore()
  'armorer': {
    pattern: null, // No UI button - auto-adds to armor score
    cardType: 'Passive',
    domain: 'valor',
    level: 5,
    description: '+1 to Armor Score while wearing armor.',
    passiveBonus: {
      type: 'armorScore',
      value: 1,
      condition: 'armored',
    },
    meta: { calculatedIn: 'characterCalculations.js', functions: ['calculateArmorScore'] },
  },

  // Rise Up (VALOR L6) - Severe threshold + reactive stress clear
  // Mechanics in calculateDamageThresholds() + useVitalsManager.js
  'rise-up': {
    pattern: null, // No UI button - auto-calculates threshold + auto-clears stress
    cardType: 'Passive',
    domain: 'valor',
    level: 6,
    description: '+Proficiency to Severe damage threshold. When you mark 1+ HP from an attack, clear 1 Stress.',
    passiveBonus: {
      type: 'threshold',
      stat: 'severeThreshold',
      formula: 'Proficiency',
      additionalEffect: 'clear 1 Stress on HP damage',
    },
    meta: {
      calculatedIn: 'characterCalculations.js',
      functions: ['calculateDamageThresholds'],
      reactiveIn: 'useVitalsManager.js',
      reactiveEffect: 'auto-clear stress on HP damage with 500ms debounce',
    },
  },

  // ---------------------------------------------------------------------------
  // COMPLEX DOMAIN CARDS (Phase C)
  // ---------------------------------------------------------------------------

  // Teleport (CODEX L5) - Long-distance teleportation with familiarity modifier
  'teleport': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'codex',
    level: 5,
    tokenId: 'teleport-charge',
    getMaxTokens: () => 1,
    refreshTrigger: 'long-rest',
    buttonLabel: {
      default: '🌀 Teleport',
      active: 'Teleporting...',
    },
    buttonIcon: '🌀',
    description: 'Once/long rest. Teleport self + willing Close targets to a visited location. Spellcast Roll (16): +3 (know well), +1 (frequent), +0 (infrequent), -2 (once). Success = arrive; Failure = off-course.',
    successMessage: 'Teleport activated! Make Spellcast Roll (16) with familiarity modifier. Success = arrive as intended.',
    actionHandler: 'activateTeleport',
    meta: {
      spellcastDC: 16,
      familiarityModifiers: [
        { label: 'Know very well', modifier: '+3' },
        { label: 'Visited frequently', modifier: '+1' },
        { label: 'Visited infrequently', modifier: '+0' },
        { label: 'Only been once', modifier: '-2' },
      ],
      oncePerLongRest: true,
    },
    tokenConfig: {
      id: 'teleport-charge',
      name: 'Teleport',
      max: 1,
      refreshOn: 'long-rest',
    },
  },

  // Reaper's Strike (BLADE L9) - Attack roll with forced damage
  'reapers-strike': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'blade',
    level: 9,
    tokenId: 'reapers-strike-charge',
    getMaxTokens: () => 1,
    refreshTrigger: 'long-rest',
    cost: { type: 'hope', amount: 1 },
    buttonLabel: {
      default: '💀 Reaper\'s Strike',
      active: 'Striking...',
    },
    buttonIcon: '💀',
    description: 'Once/long rest, spend 1 Hope. Make attack roll - GM reveals valid targets. Pick one target and force 5 HP damage.',
    successMessage: 'Reaper\'s Strike! Make attack roll. GM tells you which targets succeed. Pick one for 5 HP forced damage.',
    actionHandler: 'activateReapersStrike',
    meta: {
      oncePerLongRest: true,
      hopeCost: 1,
      forcedDamage: 5,
    },
    tokenConfig: {
      id: 'reapers-strike-charge',
      name: 'Reaper\'s Strike',
      max: 1,
      refreshOn: 'long-rest',
      cost: { type: 'hope', amount: 1 },
    },
  },

  // ---------------------------------------------------------------------------
  // Phase C.3: Stance & Party Cards
  // ---------------------------------------------------------------------------

  'hold-the-line': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'valor',
    level: 9,
    cost: { type: 'hope', amount: 1 },
    stateKey: 'holdTheLineActive',
    buttonLabel: {
      default: '🛡️ Hold the Line',
      active: '⚔️ Stance Active',
    },
    buttonIcon: '🛡️',
    description: 'Spend 1 Hope to take defensive stance. Adversaries moving within Very Close range are pulled to Melee and Restrained. Ends when you move, fail a roll with Fear, or GM spends 2 Fear.',
    successMessage: 'Hold the Line active! Adversaries entering Very Close are pulled to Melee and Restrained.',
    endMessage: 'Hold the Line ended.',
    actionHandler: 'toggleHoldTheLine',
    meta: {
      isStance: true,
      hopeCost: 1,
      endConditions: ['move', 'fearFail', 'gmClear'],
      effect: { pullTo: 'melee', condition: 'restrained' },
    },
  },

  'lead-by-example': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'valor',
    level: 9,
    cost: { type: 'stress', amount: 1 },
    buttonLabel: {
      default: '🎖️ Lead by Example',
      active: 'Inspiring...',
    },
    buttonIcon: '🎖️',
    description: 'When you deal damage to an adversary: mark 1 Stress and encourage allies. Next PC attacking that adversary can clear 1 Stress or gain 1 Hope.',
    successMessage: 'Lead by Example! Next ally attacking this adversary can choose: Clear 1 Stress OR Gain 1 Hope.',
    actionHandler: 'activateLeadByExample',
    meta: {
      trigger: 'onDamageDealt',
      stressCost: 1,
      allyBenefit: {
        trigger: 'nextAttackOnTarget',
        choices: [
          { id: 'clearStress', label: 'Clear 1 Stress', icon: '💙' },
          { id: 'gainHope', label: 'Gain 1 Hope', icon: '✨' },
        ],
      },
    },
  },

  'encore': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 10,
    buttonLabel: {
      default: '🎭 Encore',
      active: 'Casting...',
    },
    buttonIcon: '🎭',
    description: 'When an ally within Close range deals damage: make Spellcast Roll against same target. On success, deal same damage. Success with Fear = vault this card.',
    successMessage: 'Encore! Make Spellcast Roll. On success, deal same damage as ally. Fear = vault card.',
    actionHandler: 'activateEncore',
    meta: {
      trigger: 'onAllyDamageDealt',
      range: 'close',
      requiresSpellcastRoll: true,
      vaultOnFear: true,
      duplicateDamage: true,
    },
  },

  // ---------------------------------------------------------------------------
  // Phase C.4: Complex Active Cards
  // ---------------------------------------------------------------------------

  'invigoration': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 10,
    buttonLabel: {
      default: '✨ Invigorate',
      active: 'Rolling...',
    },
    buttonIcon: '✨',
    description: 'Target exhausted feature (yours or Close ally). Spend any number of Hope, roll that many d6s. Any 6 = feature resets.',
    successMessage: 'Invigoration! Select exhausted feature, spend Hope, roll d6s. Any 6 resets the feature.',
    actionHandler: 'activateInvigoration',
    meta: {
      range: 'close',
      targetType: 'exhaustedFeature',
      hopeCost: 'variable',
      dicePool: 'd6',
      successCondition: 'any6',
    },
  },

  'deathrun': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Ability',
    domain: 'bone',
    level: 10,
    cost: { type: 'hope', amount: 3 },
    buttonLabel: {
      default: '💀 Deathrun',
      active: 'Running...',
    },
    buttonIcon: '💀',
    description: 'Spend 3 Hope. Run straight path to Far range. Attack all adversaries along the way. First target: weapon damage +1 Proficiency. Each subsequent target: remove 1 die.',
    successMessage: 'Deathrun! Run to Far range, attacking all in path. Roll weapon damage +1 Prof for first, -1 die per subsequent target.',
    actionHandler: 'activateDeathrun',
    meta: {
      hopeCost: 3,
      range: 'far',
      attackType: 'weapon',
      damageModifier: { type: 'proficiency', amount: 1 },
      diminishingDice: true,
      noRepeatTargets: true,
    },
  },

  'resurrection': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'splendor',
    level: 10,
    buttonLabel: {
      default: '🌟 Resurrect',
      active: 'Casting...',
      cooldown: 'On Cooldown',
    },
    buttonIcon: '🌟',
    cooldownKey: 'resurrectionCooldown',
    cooldownSessions: 7,
    description: 'Spellcast Roll (20). Success: restore dead creature (≤100 years) to full strength, then roll d6. 1-5 = vault permanently. Failure: 7-session cooldown.',
    successMessage: 'Resurrection cast! On success, roll d6: 1-5 = vault permanently, 6 = keep. On failure, 7-session cooldown.',
    actionHandler: 'activateResurrection',
    meta: {
      spellcastDC: 20,
      targetType: 'deadCreature',
      maxDeathDuration: '100 years',
      postSuccessRoll: { die: 'd6', vaultOn: [1, 2, 3, 4, 5] },
      failureCooldown: { type: 'sessions', count: 7 },
    },
  },

  // ---------------------------------------------------------------------------
  // Phase C.5: Complex Pattern Cards
  // ---------------------------------------------------------------------------

  'adjust-reality': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'arcana',
    level: 10,
    cost: { type: 'hope', amount: 5 },
    buttonLabel: {
      default: '🔮 Adjust Reality',
      active: 'Adjusting...',
    },
    buttonIcon: '🔮',
    description: 'After any roll by you or willing ally: spend 5 Hope to change the numerical result to any plausible value within dice range.',
    successMessage: 'Adjust Reality! Change the roll result to any plausible value. Must be within possible dice range.',
    actionHandler: 'activateAdjustReality',
    meta: {
      hopeCost: 5,
      trigger: 'afterAnyRoll',
      targetType: 'rollResult',
      constraint: 'plausibleResult',
    },
  },

  'safe-haven': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'codex',
    level: 8,
    cost: { type: 'hope', amount: 2 },
    stateKey: 'safeHavenActive',
    buttonLabel: {
      default: '🏠 Summon Haven',
      active: '🚪 Haven Active',
    },
    buttonIcon: '🏠',
    description: 'Requires calm. Spend 2 Hope to summon interdimensional home. Only chosen creatures can enter. Can make entrance invisible. Resting inside = extra downtime move.',
    successMessage: 'Safe Haven summoned! Door appears within Close range. Only your chosen creatures can enter.',
    endMessage: 'Safe Haven dismissed.',
    actionHandler: 'toggleSafeHaven',
    meta: {
      hopeCost: 2,
      requirement: 'calmFocus',
      range: 'close',
      isLocation: true,
      bonus: { type: 'downtimeMoves', amount: 1, condition: 'restInside' },
    },
  },

  'copycat': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'grace',
    level: 9,
    tokenId: 'copycat-charge',
    getMaxTokens: () => 1,
    refreshTrigger: 'long-rest',
    buttonLabel: {
      default: '🎭 Copycat',
      active: 'Mimicking...',
    },
    buttonIcon: '🎭',
    description: 'Once/long rest. Mimic another player\'s domain card (level ≤8). Spend Hope = half card level (round up). Lasts until rest or they vault.',
    successMessage: 'Copycat! Select ally, choose their card (level 8 or lower), pay Hope = half level.',
    actionHandler: 'activateCopycat',
    meta: {
      oncePerLongRest: true,
      targetType: 'allyCard',
      maxCardLevel: 8,
      hopeCost: 'halfLevel',
      duration: 'untilRestOrVault',
    },
    tokenConfig: {
      id: 'copycat-charge',
      name: 'Copycat',
      max: 1,
      refreshOn: 'long-rest',
    },
  },

  'transcendent-union': {
    pattern: PATTERN_TYPES.SINGLE_ACTION,
    cardType: 'Spell',
    domain: 'codex',
    level: 10,
    tokenId: 'transcendent-union-charge',
    getMaxTokens: () => 1,
    refreshTrigger: 'long-rest',
    cost: { type: 'hope', amount: 5 },
    stateKey: 'transcendentUnionActive',
    buttonLabel: {
      default: '🔗 Unite',
      active: '🔗 Union Active',
    },
    buttonIcon: '🔗',
    description: 'Once/long rest, 5 Hope. Link 2+ willing creatures. Until next rest: when any linked creature would mark Stress/HP, all choose who takes it.',
    successMessage: 'Transcendent Union! Linked creatures can redistribute incoming damage and stress.',
    endMessage: 'Transcendent Union ended.',
    actionHandler: 'activateTranscendentUnion',
    meta: {
      oncePerLongRest: true,
      hopeCost: 5,
      targetType: 'multipleWilling',
      minTargets: 2,
      duration: 'untilRest',
      effect: 'damageRedirect',
    },
    tokenConfig: {
      id: 'transcendent-union-charge',
      name: 'Transcendent Union',
      max: 1,
      refreshOn: 'long-rest',
      cost: { type: 'hope', amount: 5 },
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get configuration for a domain card
 * @param {string} cardKey - The card key (e.g., 'conjure-swarm')
 * @param {object} card - The card data from allData.domainCards
 * @returns {object|null} The card configuration or null if not configured
 */
export const getDomainCardConfig = (cardKey, card) => {
  // Try exact key match first
  if (DOMAIN_CARD_CONFIG[cardKey]) {
    return DOMAIN_CARD_CONFIG[cardKey];
  }

  // Try card.id match (for cards loaded from SRD data)
  if (card?.id) {
    const normalizedId = card.id
      .replace('core_domain_card_', '')
      .replace(/_/g, '-');
    if (DOMAIN_CARD_CONFIG[normalizedId]) {
      return DOMAIN_CARD_CONFIG[normalizedId];
    }
  }

  return null;
};

/**
 * Check if a domain card has special configuration
 * @param {string} cardKey - The card key
 * @param {object} card - The card data
 * @returns {boolean} True if the card has special configuration
 */
export const hasSpecialConfig = (cardKey, card) => {
  return getDomainCardConfig(cardKey, card) !== null;
};

/**
 * Get list of all configured card keys
 * @returns {string[]} Array of card keys
 */
export const getConfiguredCardKeys = () => {
  return Object.keys(DOMAIN_CARD_CONFIG);
};

/**
 * Get cards by pattern type
 * @param {string} pattern - The pattern type (from PATTERN_TYPES)
 * @returns {object} Object mapping cardKey to config for matching cards
 */
export const getCardsByPattern = (pattern) => {
  return Object.entries(DOMAIN_CARD_CONFIG)
    .filter(([_, config]) => config.pattern === pattern)
    .reduce((acc, [key, config]) => ({ ...acc, [key]: config }), {});
};

export default DOMAIN_CARD_CONFIG;
